import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authStore, Benutzer, Mandant } from './authStore';
import { ElementTyp } from '../types/elemente';
import { log } from '../utils/protokoll';

// Immer relative URL: in Produktion liefert das Backend das Frontend aus, in der
// Entwicklung greift der Vite-Proxy (frontend/vite.config.ts -> localhost:3401).
// Bewusst KEINE absolute Dev-URL mehr: sie umging den Proxy und erzeugte
// Cross-Origin-Requests, unter denen die geplanten httpOnly/SameSite=Strict-
// Refresh-Cookies (Phase 0) nicht gesetzt werden koennen.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    // Noetig, damit das httpOnly-Refresh-Cookie mitgeschickt wird.
    withCredentials: true,
});

/** Fehler mit Feldmeldungen aus der Validierung (Statuscode 422). */
export class ValidierungsFehler extends Error {
    constructor(
        public readonly details: Array<{ feld: string; meldung: string }>,
        public readonly ursache: unknown
    ) {
        super(details.map((d) => `${d.feld}: ${d.meldung}`).join(', ') || 'Validierungsfehler');
        this.name = 'ValidierungsFehler';
    }
}

/**
 * Laeuft gerade eine Erneuerung? Die Promise wird geteilt.
 *
 * Ohne dieses Teilen waere der haeufigste Fehler bei rotierenden
 * Refresh-Tokens gebaut: das Dashboard startet rund acht Abfragen
 * gleichzeitig, die nach Ablauf des Tokens alle zusammen 401 bekommen. Acht
 * parallele Erneuerungen wuerden die Wiederverwendungserkennung des Servers
 * ausloesen, die daraufhin die ganze Sitzung widerruft -- der Benutzer
 * flaege nach jedem Tabwechsel raus.
 *
 * ## Warum ALLE Erneuerungen hier durchmuessen
 *
 * Genau dieser Fehler war gebaut, nur an einer anderen Stelle: die
 * Sperre lag ausschliesslich im Antwort-Abfangjaeger. Die stille
 * Erneuerung beim Start (`AuthContext`) rief `api.post('/auth/refresh')`
 * direkt auf und lief daran vorbei.
 *
 * Aufgefallen ist es, weil React im Entwicklungsbetrieb (`StrictMode`)
 * jeden Effekt doppelt ausfuehrt: bei JEDEM Seitenaufruf gingen damit
 * zwei Erneuerungen raus. Gemessen: die erste bekam 200, die zweite 401 --
 * und mit der Wiederverwendungserkennung war die gesamte Tokenfamilie
 * widerrufen. Der Benutzer landete auf der Anmeldemaske, ohne etwas
 * getan zu haben.
 *
 * Im Produktivbetrieb laeuft der Effekt nur einmal, das Rennen bleibt
 * aber moeglich: die Start-Erneuerung und eine durch 401 ausgeloeste
 * koennen sich ueberlappen. Dann traefe es den Benutzer selten und
 * unerklaerlich -- die unangenehmste Sorte Fehler.
 */
let erneuerungLaeuft: Promise<RohSitzung> | null = null;

/** Was /auth/refresh zurueckliefert. Genauer als `any` und hier ausreichend. */
interface RohSitzung {
    access_token: string;
    [feld: string]: unknown;
}

/**
 * Die EINZIGE Stelle, an der /auth/refresh aufgerufen wird.
 *
 * Laeuft bereits eine Erneuerung, wird deren Promise geteilt statt eine
 * zweite zu starten.
 */
export function erneuerungAnfordern(): Promise<RohSitzung> {
    erneuerungLaeuft ??= api
        .post('/auth/refresh')
        .then((r) => {
            const daten = r.data as RohSitzung;
            authStore.tokenErneuern(daten.access_token);
            return daten;
        })
        .catch((e) => {
            authStore.leeren();
            beiAbmeldung?.();
            throw e;
        })
        .finally(() => {
            erneuerungLaeuft = null;
        });

    return erneuerungLaeuft;
}

/** Wird gesetzt, sobald die Anmeldung endgueltig verloren ist. */
let beiAbmeldung: (() => void) | null = null;

export function setzeAbmeldeBehandlung(fn: () => void): void {
    beiAbmeldung = fn;
}

api.interceptors.request.use((cfg) => {
    const token = authStore.accessToken;
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

/**
 * Meldet einen API-Fehler an GlitchTip -- aber nur den, der ein Vorfall
 * ist, nicht jeden erwarteten 4xx.
 *
 * Ein 404/403/409/422 ist bereits ein behandelter Fachfall (Validierung,
 * fehlendes Recht, "nicht gefunden" statt einer Existenzauskunft) und
 * wird an der jeweiligen Stelle im UI angezeigt -- das als Vorfall zu
 * melden waere reines Rauschen, dieselbe Abwaegung wie beim Ausschluss
 * der Backend-Zugriffszeile (siehe backend/src/utils/protokoll.ts).
 * Ein 5xx oder ein Fehler ganz ohne Antwort (Netzwerkausfall, CORS,
 * Zeitueberschreitung) ist dagegen unerwartet und gehoert dokumentiert.
 */
function meldeUnerwartetenApiFehler(fehler: AxiosError<any>, status?: number): void {
    if (status !== undefined && status < 500) return;
    const methode = fehler.config?.method?.toUpperCase() ?? '?';
    const pfad = fehler.config?.url ?? '?';
    log.error(`API-Fehler ${methode} ${pfad}`, fehler);
}

api.interceptors.response.use(
    (r) => r,
    async (fehler: AxiosError<any>) => {
        const cfg = fehler.config as (InternalAxiosRequestConfig & { _wiederholt?: boolean }) | undefined;
        const status = fehler.response?.status;

        if (status === 422 && Array.isArray(fehler.response?.data?.details)) {
            throw new ValidierungsFehler(fehler.response!.data.details, fehler);
        }

        // Gesperrter oder gekuendigter Mandant: der Server weist jede Anfrage
        // mit 403 ab. Ohne diese Stelle zeigte die Oberflaeche bis zum
        // Tokenablauf nur Fehler. Die Mobil-App ist davon nicht betroffen.
        if (
            status === 403 &&
            fehler.response?.data?.code === 'MANDANT_GESPERRT' &&
            !cfg?.url?.startsWith('/auth/')
        ) {
            authStore.leeren();
            window.location.assign('/login?gesperrt=1');
            throw fehler;
        }

        // Auth-Endpunkte sind ausgenommen, sonst dreht sich die Erneuerung
        // im Kreis: ein 401 aus /auth/refresh wuerde eine Erneuerung
        // ausloesen, die wieder 401 liefert.
        if (status !== 401 || !cfg || cfg._wiederholt || cfg.url?.startsWith('/auth/')) {
            meldeUnerwartetenApiFehler(fehler, status);
            throw fehler;
        }

        cfg._wiederholt = true;

        const { access_token: neuesToken } = await erneuerungAnfordern();
        cfg.headers.Authorization = `Bearer ${neuesToken}`;
        return api(cfg);
    }
);

export interface AnmeldeAntwort {
    mfa_erforderlich?: boolean;
    mfa_token?: string;
    ziel_email_maskiert?: string;
    access_token?: string;
    benutzer?: Benutzer;
    mandant?: Mandant;
}

export const authApi = {
    registrieren: (data: {
        firma_name: string;
        email: string;
        passwort: string;
        vorname?: string;
        nachname: string;
    }) => api.post('/auth/register', data).then((r) => r.data),

    emailBestaetigen: (data: { benutzer_id: number; code: string }) =>
        api.post('/auth/email/bestaetigen', data).then((r) => r.data),

    anmelden: (data: { email: string; passwort: string }): Promise<AnmeldeAntwort> =>
        api.post('/auth/login', data).then((r) => r.data),

    mfaBestaetigen: (data: {
        mfa_token: string;
        code: string;
        geraet_vertrauen?: boolean;
        geraete_name?: string;
    }) => api.post('/auth/mfa/verify', data).then((r) => r.data),

    mfaErneutSenden: (data: { mfa_token: string }) =>
        api.post('/auth/mfa/erneut-senden', data).then((r) => r.data),

    /*
     * Ueber die gemeinsame Sperre, NICHT direkt: sonst laeuft die stille
     * Erneuerung beim Start an ihr vorbei und kann sich mit einer durch
     * 401 ausgeloesten ueberlappen. Beide legen dasselbe rotierende
     * Token vor, und der Server widerruft daraufhin die ganze Familie.
     */
    erneuern: () => erneuerungAnfordern(),
    abmelden: (alleGeraete = false) =>
        api.post('/auth/logout', { alle_geraete: alleGeraete }).then((r) => r.data),
    ich: () => api.get('/auth/me').then((r) => r.data),
    ichAendern: (data: Record<string, unknown>) => api.patch('/auth/me', data).then((r) => r.data),

    passwortAendern: (data: { altes_passwort: string; neues_passwort: string }) =>
        api.post('/auth/passwort/aendern', data).then((r) => r.data),
    passwortVergessen: (data: { email: string }) =>
        api.post('/auth/passwort/vergessen', data).then((r) => r.data),
    passwortZuruecksetzen: (data: { benutzer_id: number; code: string; neues_passwort: string }) =>
        api.post('/auth/passwort/zuruecksetzen', data).then((r) => r.data),

    sitzungen: () => api.get('/auth/sitzungen').then((r) => r.data),
    sitzungWiderrufen: (id: number) => api.delete(`/auth/sitzungen/${id}`).then((r) => r.data),
    alleAnderenWiderrufen: () => api.delete('/auth/sitzungen').then((r) => r.data),
};

export interface Berechtigung {
    schluessel: string;
    modul: string;
    bezeichnung: string;
    beschreibung: string | null;
    impliziert: string[];
    gefahr: 'NORMAL' | 'ERHOEHT' | 'KRITISCH';
}

export interface Rolle {
    id: number;
    schluessel: string;
    name: string;
    beschreibung: string | null;
    ist_systemvorlage: boolean;
    ist_geschuetzt: boolean;
    benutzer_anzahl: number;
    rechte_gesetzt: string[];
    rechte_wirksam: string[];
}

export const rollenApi = {
    katalog: (): Promise<{ module: Array<{ schluessel: string; bezeichnung: string }>; berechtigungen: Berechtigung[] }> =>
        api.get('/rollen/katalog').then((r) => r.data),
    meineRechte: () => api.get('/rollen/meine-rechte').then((r) => r.data),
    auflisten: (): Promise<Rolle[]> => api.get('/rollen').then((r) => r.data),
    anlegen: (data: { name: string; beschreibung?: string; vorlage_id?: number }) =>
        api.post('/rollen', data).then((r) => r.data),
    aendern: (id: number, data: { name?: string; beschreibung?: string }) =>
        api.patch(`/rollen/${id}`, data).then((r) => r.data),
    rechteSetzen: (id: number, rechte: string[]) =>
        api.put(`/rollen/${id}/rechte`, { rechte }).then((r) => r.data),
    loeschen: (id: number, mitZuweisungen = false) =>
        api
            .delete(`/rollen/${id}${mitZuweisungen ? '?mitZuweisungen=1' : ''}`)
            .then((r) => r.data),
    rollenVonBenutzer: (benutzerId: number): Promise<Rolle[]> =>
        api.get(`/rollen/benutzer/${benutzerId}`).then((r) => r.data),
    rollenSetzen: (benutzerId: number, rollen: number[]) =>
        api.put(`/rollen/benutzer/${benutzerId}`, { rollen }).then((r) => r.data),
};

export interface Mitarbeiter {
    id: number;
    benutzer_id: number | null;
    name: string;
    titel: string | null;
    qualifikation: string | null;
    zertifizierung: string | null;
    zertifikat_gueltig_bis: string | null;
    personalnummer: string | null;
    email: string | null;
    telefon: string | null;
    aktiv: boolean;
    hat_zugang: boolean;
    konto_status: string | null;
    letzter_login_am: string | null;
    /** Code per Mail bei der Anmeldung; `null` ohne Konto. */
    mfa_pflicht: boolean | null;
}

/** Erste Ausgabe eines Kopplungscodes -- der Klartext ist NUR hier sichtbar. */
export interface Kopplungscode {
    id: number;
    code: string;
    code_praefix: string;
    laeuft_ab: string;
}

export const mitarbeiterApi = {
    auflisten: (): Promise<{
        mitarbeiter: Mitarbeiter[];
        konten_ohne_person: Array<{
            benutzer_id: number;
            // `null` bei einem Konto ohne Postfach (V433/V434).
            email: string | null;
            name: string;
            konto_status: string;
            letzter_login_am: string | null;
            mfa_pflicht: boolean;
        }>;
    }> => api.get('/mitarbeiter').then((r) => r.data),

    zertifikate: (tage = 90) =>
        api.get('/mitarbeiter/zertifikate', { params: { tage } }).then((r) => r.data),

    einladungen: () => api.get('/mitarbeiter/einladungen').then((r) => r.data),
    einladen: (data: {
        email: string;
        vorname?: string;
        nachname: string;
        rollen: number[];
    }) => api.post('/mitarbeiter/einladungen', data).then((r) => r.data),
    einladungWiderrufen: (id: number) =>
        api.delete(`/mitarbeiter/einladungen/${id}`).then((r) => r.data),

    deaktivieren: (benutzerId: number) =>
        api.post(`/mitarbeiter/${benutzerId}/deaktivieren`).then((r) => r.data),
    aktivieren: (benutzerId: number) =>
        api.post(`/mitarbeiter/${benutzerId}/aktivieren`).then((r) => r.data),

    /**
     * Dritter Weg, ein Konto anzulegen (M.4): ohne Postfach. Legt das Konto
     * an und erzeugt im selben Zug den ersten Kopplungscode -- ohne ihn
     * waere das Konto fuer niemanden erreichbar.
     */
    ohnePostfachAnlegen: (data: {
        vorname?: string;
        nachname: string;
        rollen: number[];
        geraete_name?: string;
    }): Promise<{
        benutzer_id: number;
        kopplungscode: Kopplungscode | null;
        hinweis?: string;
    }> => api.post('/mitarbeiter/ohne-postfach', data).then((r) => r.data),

    kopplungscodeErzeugen: (benutzerId: number, geraeteName?: string): Promise<Kopplungscode> =>
        api
            .post(`/mitarbeiter/${benutzerId}/kopplungscodes`, { geraete_name: geraeteName ?? null })
            .then((r) => r.data),
};

export const einladungApi = {
    pruefen: (token: string) => api.get(`/einladung/${token}`).then((r) => r.data),
    annehmen: (data: { token: string; passwort: string }) =>
        api.post('/einladung/annehmen', data).then((r) => r.data),
};

export interface FotoAnnotation {
    id: number;
    form:
        | 'RECHTECK'
        | 'ELLIPSE'
        | 'PFEIL'
        | 'LINIE'
        | 'FREIHAND'
        | 'TEXT'
        | 'MASS'
        | 'SCHWAERZUNG';
    geometrie: any;
    text: string | null;
    farbe: string;
    strichstaerke: number;
    nummer: number | null;
    /** Was davon im Kundendokument erscheint. */
    sichtbarkeit: 'KUNDE' | 'INTERN' | 'VERTRAULICH';
    mass_mm: number | null;
    /** Woran gemessen wurde -- ohne Merkmal gibt es keinen Grenzwert dazu. */
    merkmal: string | null;
    /** `null` heisst "nicht bewertet" und ausdruecklich nicht "in Ordnung". */
    bewertung: 'INNERHALB' | 'UEBERSCHRITTEN' | 'UNTERSCHRITTEN' | null;
    herkunft: 'MENSCH' | 'KI_VORSCHLAG' | 'KI_BESTAETIGT';
    ki_zuversicht: number | null;
}

export interface MassGrenzwert {
    id: number;
    merkmal: string;
    pruefbereich_code: string | null;
    min_mm: number | null;
    max_mm: number | null;
    norm_referenz: string | null;
    hinweis: string | null;
    aktiv: boolean;
}

export const massGrenzwerteApi = {
    liste: (): Promise<MassGrenzwert[]> =>
        api.get('/mass-grenzwerte').then((r) => r.data),
    anlegen: (data: Partial<MassGrenzwert>) =>
        api.post('/mass-grenzwerte', data).then((r) => r.data),
    aendern: (id: number, data: Partial<MassGrenzwert>) =>
        api.put(`/mass-grenzwerte/${id}`, data).then((r) => r.data),
    loeschen: (id: number) => api.delete(`/mass-grenzwerte/${id}`).then((r) => r.data),
};

export const medienApi = {
    /**
     * Hochladen läuft als multipart -- deshalb OHNE den JSON-Content-Type
     * der Instanz. Setzt man ihn, fehlt die boundary und der Server sieht
     * einen leeren Rumpf.
     */
    hochladen: (
        dateien: File[],
        bezug: { element_id?: number; mangel_id?: number; projekt_id?: number },
        zusatz?: { kategorie?: string; beschreibung?: string; sichtbarkeit?: string }
    ) => {
        const daten = new FormData();
        for (const d of dateien) daten.append('fotos', d);
        for (const [k, v] of Object.entries({ ...bezug, ...zusatz })) {
            if (v !== undefined && v !== null) daten.append(k, String(v));
        }
        return api
            .post('/medien', daten, { headers: { 'Content-Type': undefined as any } })
            .then((r) => r.data);
    },

    loeschen: (id: number) => api.delete(`/medien/${id}`).then((r) => r.data),

    annotationen: (
        fotoId: number
    ): Promise<{
        foto: {
            id: number;
            breite: number;
            hoehe: number;
            massstab_px_pro_mm: number | null;
            massstab_referenz: string | null;
        };
        annotationen: FotoAnnotation[];
    }> => api.get(`/medien/${fotoId}/annotationen`).then((r) => r.data),

    annotationAnlegen: (fotoId: number, data: Record<string, unknown>) =>
        api.post(`/medien/${fotoId}/annotationen`, data).then((r) => r.data),

    annotationAendern: (id: number, data: Record<string, unknown>) =>
        api.patch(`/medien/annotationen/${id}`, data).then((r) => r.data),

    annotationLoeschen: (id: number) =>
        api.delete(`/medien/annotationen/${id}`).then((r) => r.data),

    massstabSetzen: (
        fotoId: number,
        data: { laenge_px: number; laenge_mm: number; referenz: string }
    ) => api.put(`/medien/${fotoId}/massstab`, data).then((r) => r.data),

    /**
     * Macht aus einem KI-Vorschlag eine Aussage.
     *
     * Ein eigener Aufruf und kein Feld in `annotationAendern`: eine
     * Bestätigung soll nicht nebenbei beim Verschieben eines Rechtecks
     * passieren.
     */
    annotationBestaetigen: (id: number) =>
        api.post(`/medien/annotationen/${id}/bestaetigen`).then((r) => r.data),

    /** Lässt die KI Markierungen vorschlagen -- bestätigungspflichtig. */
    kiMarkierungen: (fotoId: number) =>
        api.post(`/medien/${fotoId}/annotationen/ki`).then((r) => r.data),
};

export type AblageTreiber = 'NEXTCLOUD' | 'WEBDAV' | 'FTP';

export interface AblageVerbindung {
    id: number;
    kunde_id: number | null;
    kunde_name: string | null;
    treiber: AblageTreiber;
    basis_url: string;
    benutzername: string;
    wurzel_ordner: string;
    ordner_muster: string;
    letzter_test_am: string | null;
    letzter_test_erfolg: boolean | null;
    letzter_test_meldung: string | null;
}

/** `kundeId` waehlt das zusaetzliche Ziel eines Kunden; ohne ist der Firmenspeicher gemeint. */
export const ablageApi = {
    lesen: (kundeId?: number | null) =>
        api.get('/ablage', { params: { kunde_id: kundeId ?? undefined } }).then((r) => r.data),
    verbindungen: (): Promise<AblageVerbindung[]> =>
        api.get('/ablage/verbindungen').then((r) => r.data),
    dateien: (grenze = 100) =>
        api.get('/ablage/dateien', { params: { grenze } }).then((r) => r.data),
    einrichten: (data: Record<string, unknown>, kundeId?: number | null) =>
        api.put('/ablage', data, { params: { kunde_id: kundeId ?? undefined } }).then((r) => r.data),
    testen: (kundeId?: number | null) =>
        api.post('/ablage/test', undefined, { params: { kunde_id: kundeId ?? undefined } }).then((r) => r.data),
    entfernen: (kundeId?: number | null) =>
        api.delete('/ablage', { params: { kunde_id: kundeId ?? undefined } }).then((r) => r.data),
};

export const mandantApi = {
    lesen: () => api.get('/mandant').then((r) => r.data),
    felder: () => api.get('/mandant/felder').then((r) => r.data),
    kennzahlen: () => api.get('/mandant/kennzahlen').then((r) => r.data),
    benutzer: () => api.get('/mandant/benutzer').then((r) => r.data),
    audit: (params: Record<string, unknown>) =>
        api.get('/mandant/audit', { params }).then((r) => r.data),
    stammdatenAendern: (data: Record<string, unknown>) =>
        api.patch('/mandant', data).then((r) => r.data),
    brandingAendern: (data: Record<string, unknown>) =>
        api.patch('/mandant/branding', data).then((r) => r.data),
    /**
     * Logo-Upload ueber die bestehende Medienpipeline (kein eigener
     * Speicherweg). Multipart ohne den JSON-Content-Type der Instanz --
     * siehe `medienApi.hochladen` fuer die Begruendung.
     */
    logoHochladen: (datei: File) => {
        const daten = new FormData();
        daten.append('logo', datei);
        return api
            .post('/mandant/logo', daten, { headers: { 'Content-Type': undefined as any } })
            .then((r) => r.data);
    },
    onboardingAbschliessen: () => api.patch('/mandant/onboarding', {}).then((r) => r.data),
    /** Zweiten Faktor (Code per Mail) fuer einen Benutzer ein- oder ausschalten. */
    mfaSetzen: (benutzerId: number, mfaPflicht: boolean) =>
        api
            .patch(`/mandant/benutzer/${benutzerId}/mfa`, { mfa_pflicht: mfaPflicht })
            .then((r) => r.data as { id: number; mfa_pflicht: boolean }),
};

export type MandantStatus = 'TESTPHASE' | 'AKTIV' | 'GESPERRT' | 'GEKUENDIGT';

export interface PlattformMandant {
    id: number;
    firma_name: string;
    slug: string;
    status: MandantStatus;
    plan_code: string;
    kontakt_email: string;
    erstellt_am: string;
    gesperrt_grund: string | null;
    benutzer_anzahl: number;
    kunden_anzahl: number;
    elemente_anzahl: number;
    letzte_anmeldung: string | null;
}

export interface PlattformBenutzer {
    id: number;
    email: string | null;
    vorname: string | null;
    nachname: string;
    status: 'EINGELADEN' | 'AKTIV' | 'DEAKTIVIERT' | 'GESPERRT';
    letzter_login_am: string | null;
    gesperrt_bis: string | null;
}

/** Plattform-Administration (nur `ist_plattform_admin`). */
export const plattformApi = {
    mandanten: (): Promise<PlattformMandant[]> =>
        api.get('/plattform/mandanten').then((r) => r.data),
    statusSetzen: (id: number, status: MandantStatus, grund?: string) =>
        api.patch(`/plattform/mandanten/${id}/status`, { status, grund }).then((r) => r.data),
    benutzer: (mandantId: number): Promise<PlattformBenutzer[]> =>
        api.get(`/plattform/mandanten/${mandantId}/benutzer`).then((r) => r.data),
    zugangAendern: (
        mandantId: number,
        benutzerId: number,
        daten: { email?: string; passwort?: string }
    ): Promise<{ id: number; email: string | null }> =>
        api.patch(`/plattform/mandanten/${mandantId}/benutzer/${benutzerId}`, daten).then((r) => r.data),
    adminAnlegen: (
        mandantId: number,
        daten: { email: string; nachname: string; vorname?: string; passwort: string }
    ): Promise<{ id: number; email: string }> =>
        api.post(`/plattform/mandanten/${mandantId}/benutzer`, daten).then((r) => r.data),
};

/** Route des eigenen Mandantenlogos -- fuer `MedienBild`/`useMedienUrl`. */
export const MANDANT_LOGO_QUELLE = '/api/mandant/logo';

export const kundenApi = {
    getAll: () => api.get('/kunden').then(res => res.data),
    getById: (id: number) => api.get(`/kunden/${id}`).then(res => res.data),
    create: (data: any) => api.post('/kunden', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/kunden/${id}`, data).then(res => res.data),
    delete: (id: number) => api.delete(`/kunden/${id}`).then(res => res.data),
};

export const standorteApi = {
    getAll: (kundeId?: number) => api.get('/standorte', { params: { kundeId } }).then(res => res.data),
    getById: (id: number) => api.get(`/standorte/${id}`).then(res => res.data),
    create: (data: any) => api.post('/standorte', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/standorte/${id}`, data).then(res => res.data),
    delete: (id: number) => api.delete(`/standorte/${id}`).then(res => res.data),
};

export const gebaeudeApi = {
    getAll: (standortId?: number) => api.get('/gebaeude', { params: { standortId } }).then(res => res.data),
    create: (data: any) => api.post('/gebaeude', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/gebaeude/${id}`, data).then(res => res.data),
    delete: (id: number) => api.delete(`/gebaeude/${id}`).then(res => res.data),
};

export const stockwerkeApi = {
    getAll: (gebaeudeId?: number, includePlaene?: boolean) =>
        api.get('/stockwerke', { params: { gebaeudeId, includePlaene } }).then(res => res.data),
    getById: (id: number) => api.get(`/stockwerke/${id}`).then(res => res.data),
    create: (data: any) => api.post('/stockwerke', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/stockwerke/${id}`, data).then(res => res.data),
    delete: (id: number) => api.delete(`/stockwerke/${id}`).then(res => res.data),

    // Floor Plans (Plaene)
    getPlaene: (stockwerkId: number) =>
        api.get(`/stockwerke/${stockwerkId}/plaene`).then(res => res.data),
    getPlanById: (planId: number) =>
        api.get(`/stockwerke/plaene/${planId}`).then(res => res.data),
    createPlan: (stockwerkId: number, bezeichnung: string, dateiUrl: string) =>
        api.post(`/stockwerke/${stockwerkId}/plaene`, { bezeichnung, datei_url: dateiUrl }).then(res => res.data),
    uploadPlan: (stockwerkId: number, file: File, bezeichnung?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        if (bezeichnung) formData.append('bezeichnung', bezeichnung);
        return api.post(`/stockwerke/${stockwerkId}/plaene/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => res.data);
    },
    updatePlan: (planId: number, bezeichnung: string) =>
        api.put(`/stockwerke/plaene/${planId}`, { bezeichnung }).then(res => res.data),
    deletePlan: (planId: number) =>
        api.delete(`/stockwerke/plaene/${planId}`).then(res => res.data),
};

export interface ElementFilter {
    stockwerkId?: number;
    gebaeudeId?: number;
    standortId?: number;
    kundeId?: number;
    typ?: ElementTyp;
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
}

export const elementeApi = {
    getAll: (stockwerkId?: number) => api.get('/elemente', { params: { stockwerkId } }).then(res => res.data),
    getAllWithDetails: (filter?: ElementFilter) => api.get('/elemente/details', { params: filter }).then(res => res.data),
    getById: (id: number) => api.get(`/elemente/${id}`).then(res => res.data),
    getByIdWithDetails: (id: number) => api.get(`/elemente/${id}/details`).then(res => res.data),
    getStatusOptions: () => api.get('/elemente/status-options').then(res => res.data),
    create: (data: any) => api.post('/elemente', data).then(res => res.data),
    createBatch: (elements: any[]) => api.post('/elemente/batch', { elements }).then(res => res.data),
    update: (id: number, data: any) => api.put(`/elemente/${id}`, data).then(res => res.data),
    delete: (id: number) => api.delete(`/elemente/${id}`).then(res => res.data),
};

export const katalogApi = {
    getAll: (katalog?: string) => api.get('/katalog', { params: { katalog } }).then(res => res.data),
    create: (data: any) => api.post('/katalog', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/katalog/${id}`, data).then(res => res.data),
    delete: (id: number) => api.delete(`/katalog/${id}`).then(res => res.data),
};

export const systemApi = {
    getSettings: () => api.get('/system').then(res => res.data),
    updateSettings: (data: any) => api.post('/system', data).then(res => res.data),
};

export const statistikApi = {
    getDashboard: () => api.get('/statistik/dashboard').then(res => res.data),
    getElemente: () => api.get('/statistik/elemente').then(res => res.data),
    getKunden: () => api.get('/statistik/kunden').then(res => res.data),
    getMaengel: () => api.get('/statistik/maengel').then(res => res.data),
    getFortschritt: () => api.get('/statistik/fortschritt').then(res => res.data),
    getAktivitaeten: (limit?: number) => api.get('/statistik/aktivitaeten', { params: { limit } }).then(res => res.data),

    // Advanced Statistics (V3)
    getTrend: (monate?: number) => api.get('/statistik/trend', { params: { monate } }).then(res => res.data),
    getHeatmap: (kundeId?: number) => api.get('/statistik/heatmap', { params: { kunde_id: kundeId } }).then(res => res.data),
    getSchweregrade: () => api.get('/statistik/schweregrade').then(res => res.data),
    getProjekteFortschritt: () => api.get('/statistik/projekte-fortschritt').then(res => res.data),
    getZeitstatistik: (von?: string, bis?: string) => api.get('/statistik/zeit', { params: { von, bis } }).then(res => res.data),
};

// ============================================================================
// Neue API-Services (V2)
// ============================================================================

export const produktkatalogApi = {
    getAll: () => api.get('/produktkatalog').then(res => res.data),
    getById: (id: number) => api.get(`/produktkatalog/${id}`).then(res => res.data),
    getByTyp: (typ: string) => api.get(`/produktkatalog/typ/${typ}`).then(res => res.data),
    getByHersteller: (hersteller: string) => api.get(`/produktkatalog/hersteller/${hersteller}`).then(res => res.data),
    search: (term: string) => api.get(`/produktkatalog/search/${term}`).then(res => res.data),
    create: (data: any) => api.post('/produktkatalog', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/produktkatalog/${id}`, data).then(res => res.data),
    delete: (id: number) => api.delete(`/produktkatalog/${id}`).then(res => res.data),
};

export const normungenApi = {
    getAll: () => api.get('/normungen').then(res => res.data),
    getById: (id: number) => api.get(`/normungen/${id}`).then(res => res.data),
    getByNormNummer: (norm_nummer: string) => api.get(`/normungen/nummer/${norm_nummer}`).then(res => res.data),
    getGueltig: () => api.get('/normungen/gueltig').then(res => res.data),
    search: (term: string) => api.get(`/normungen/search/${term}`).then(res => res.data),
    create: (data: any) => api.post('/normungen', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/normungen/${id}`, data).then(res => res.data),
    delete: (id: number) => api.delete(`/normungen/${id}`).then(res => res.data),
};

export const projekteApi = {
    getAll: () => api.get('/projekte').then(res => res.data),
    getById: (id: number) => api.get(`/projekte/${id}`).then(res => res.data),
    getByKunde: (kunde_id: number) => api.get(`/projekte/kunde/${kunde_id}`).then(res => res.data),
    getByStatus: (status: string) => api.get(`/projekte/status/${status}`).then(res => res.data),
    generateNr: () => api.get('/projekte/generate-nr').then(res => res.data),
    create: (data: any) => api.post('/projekte', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/projekte/${id}`, data).then(res => res.data),
    updateStatus: (id: number, status: string) => api.patch(`/projekte/${id}/status`, { status }).then(res => res.data),
    updateStatistics: (id: number) => api.patch(`/projekte/${id}/statistics`).then(res => res.data),
    delete: (id: number) => api.delete(`/projekte/${id}`).then(res => res.data),

    // Meilensteine
    addMeilenstein: (id: number, data: any) => api.post(`/projekte/${id}/meilensteine`, data).then(res => res.data),
    updateMeilenstein: (meilenstein_id: number, data: any) => api.put(`/projekte/meilensteine/${meilenstein_id}`, data).then(res => res.data),
    deleteMeilenstein: (meilenstein_id: number) => api.delete(`/projekte/meilensteine/${meilenstein_id}`).then(res => res.data),

    // Element-Zuordnung
    getElemente: (id: number) => api.get(`/projekte/${id}/elemente`).then(res => res.data),
    addElement: (id: number, element_id: number, notizen?: string) =>
        api.post(`/projekte/${id}/elemente`, { element_id, notizen }).then(res => res.data),
    addElementeBatch: (id: number, element_ids: number[]) =>
        api.post(`/projekte/${id}/elemente/batch`, { element_ids }).then(res => res.data),
    removeElement: (id: number, element_id: number) =>
        api.delete(`/projekte/${id}/elemente/${element_id}`).then(res => res.data),
    updatePruefErgebnis: (id: number, element_id: number, pruef_ergebnis: string, notizen?: string) =>
        api.put(`/projekte/${id}/elemente/${element_id}`, { pruef_ergebnis, notizen }).then(res => res.data),
};

export const maengelApi = {
    getAll: (filter?: { kunde_id?: number }) => api.get('/maengel', { params: filter }).then(res => res.data),
    getById: (id: number) => api.get(`/maengel/${id}`).then(res => res.data),
    getByElement: (element_id: number) => api.get(`/maengel/element/${element_id}`).then(res => res.data),
    getByProjekt: (projekt_id: number) => api.get(`/maengel/projekt/${projekt_id}`).then(res => res.data),
    getByStatus: (status: string) => api.get(`/maengel/status/${status}`).then(res => res.data),
    getBySchweregrad: (schweregrad: string) => api.get(`/maengel/schweregrad/${schweregrad}`).then(res => res.data),
    getUeberfaellig: () => api.get('/maengel/ueberfaellig').then(res => res.data),
    create: (data: any) => api.post('/maengel', data).then(res => res.data),
    createWithAI: (data: any) => api.post('/maengel/generate', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/maengel/${id}`, data).then(res => res.data),
    updateStatus: (id: number, status: string, kommentar?: string) => api.patch(`/maengel/${id}/status`, { status, kommentar }).then(res => res.data),
    delete: (id: number) => api.delete(`/maengel/${id}`).then(res => res.data),
};

export const fotosApi = {
    getAll: () => api.get('/fotos').then(res => res.data),
    getById: (id: number) => api.get(`/fotos/${id}`).then(res => res.data),
    getByElement: (element_id: number) => api.get(`/fotos/element/${element_id}`).then(res => res.data),
    getByMangel: (mangel_id: number) => api.get(`/fotos/mangel/${mangel_id}`).then(res => res.data),
    getByProjekt: (projekt_id: number) => api.get(`/fotos/projekt/${projekt_id}`).then(res => res.data),
    getWithGPS: () => api.get('/fotos/gps').then(res => res.data),
    create: (data: any) => api.post('/fotos', data).then(res => res.data),
    analyze: (id: number) => api.post(`/fotos/${id}/analyze`).then(res => res.data),
    update: (id: number, data: any) => api.put(`/fotos/${id}`, data).then(res => res.data),
    updateAnnotationen: (id: number, annotationen: any[]) => api.patch(`/fotos/${id}/annotationen`, { annotationen }).then(res => res.data),
    delete: (id: number) => api.delete(`/fotos/${id}`).then(res => res.data),
};

export const stellungnahmenApi = {
    getAll: () => api.get('/stellungnahmen').then(res => res.data),
    getById: (id: number) => api.get(`/stellungnahmen/${id}`).then(res => res.data),
    getByProjekt: (projekt_id: number) => api.get(`/stellungnahmen/projekt/${projekt_id}`).then(res => res.data),
    getByStatus: (status: string) => api.get(`/stellungnahmen/status/${status}`).then(res => res.data),
    create: (data: any) => api.post('/stellungnahmen', data).then(res => res.data),
    generateWithAI: (data: any) => api.post('/stellungnahmen/generate', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/stellungnahmen/${id}`, data).then(res => res.data),
    updateStatus: (id: number, status: string, freigegeben_von?: string) => api.patch(`/stellungnahmen/${id}/status`, { status, freigegeben_von }).then(res => res.data),
    delete: (id: number) => api.delete(`/stellungnahmen/${id}`).then(res => res.data),
};

export const textbausteineApi = {
    getAll: () => api.get('/textbausteine').then(res => res.data),
    getById: (id: number) => api.get(`/textbausteine/${id}`).then(res => res.data),
    getByKategorie: (kategorie: string) => api.get(`/textbausteine/kategorie/${kategorie}`).then(res => res.data),
    getFavoriten: () => api.get('/textbausteine/favoriten').then(res => res.data),
    getHaeufigVerwendet: (limit?: number) => api.get('/textbausteine/haeufig', { params: { limit } }).then(res => res.data),
    search: (term: string) => api.get(`/textbausteine/search/${term}`).then(res => res.data),
    create: (data: any) => api.post('/textbausteine', data).then(res => res.data),
    generateWithAI: (data: any) => api.post('/textbausteine/generate', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/textbausteine/${id}`, data).then(res => res.data),
    incrementVerwendung: (id: number) => api.patch(`/textbausteine/${id}/verwendung`).then(res => res.data),
    toggleFavorit: (id: number) => api.patch(`/textbausteine/${id}/favorit`).then(res => res.data),
    delete: (id: number) => api.delete(`/textbausteine/${id}`).then(res => res.data),

    // Checklisten
    getAllChecklisten: () => api.get('/textbausteine/checklisten/all').then(res => res.data),
    getChecklisteById: (id: number) => api.get(`/textbausteine/checklisten/${id}`).then(res => res.data),
    createCheckliste: (data: any) => api.post('/textbausteine/checklisten', data).then(res => res.data),
    updateCheckliste: (id: number, data: any) => api.put(`/textbausteine/checklisten/${id}`, data).then(res => res.data),
    deleteCheckliste: (id: number) => api.delete(`/textbausteine/checklisten/${id}`).then(res => res.data),
};

export const reportsApi = {
    downloadStandardReport: (filter?: { kundeId?: number; standortId?: number; gebaeudeId?: number; stockwerkId?: number }) =>
        api.get('/reports/standard', { params: filter, responseType: 'blob' }).then(res => res.data),
    downloadElementReport: (id: number) =>
        api.get(`/reports/element/${id}`, { responseType: 'blob' }).then(res => res.data),
    downloadMaengelReport: (filter?: { status?: string; schweregrad?: string; elementId?: number }) =>
        api.get('/reports/maengel', { params: filter, responseType: 'blob' }).then(res => res.data),
    downloadSummaryReport: () =>
        api.get('/reports/summary', { responseType: 'blob' }).then(res => res.data),
    downloadElementGutachten: (elementId: number) =>
        api.get(`/reports/gutachten/element/${elementId}`, { responseType: 'blob' }).then(res => res.data),
    downloadGebaeudeGutachten: (gebaeudeId: number) =>
        api.get(`/reports/gutachten/gebaeude/${gebaeudeId}`, { responseType: 'blob' }).then(res => res.data),
};

/**
 * Dokumente aus der HTML-Vorlagenschicht.
 *
 * Vorschau und PDF sind Zwillinge derselben Vorlage -- was hier als
 * Vorschau erscheint, ist genau das, was im PDF steht. Deshalb sind es
 * auch hier zwei Aufrufe auf denselben Pfad und nicht zwei Endpunkte
 * mit eigener Logik.
 */
export const dokumenteApi = {
    /** Welche Dokumenttypen gibt es und welche Parameter kennen sie? */
    typen: (): Promise<DokumentTyp[]> =>
        api.get('/dokumente/typen').then(res => res.data),

    /** Zustand des Renderers -- fuer die Diagnose, wenn kein PDF entsteht. */
    renderer: () => api.get('/dokumente/renderer').then(res => res.data),

    /** HTML der Vorschau. Wird in einem abgeschotteten Rahmen angezeigt. */
    vorschau: (typ: string, parameter: Record<string, string | number | undefined> = {}): Promise<string> =>
        api.get(`/dokumente/${typ}/vorschau`, { params: parameter }).then(res => res.data),

    /** Das fertige PDF. `ablegen` legt es zusaetzlich im Firmenspeicher ab. */
    pdf: (
        typ: string,
        parameter: Record<string, string | number | undefined> = {},
        ablegen = false
    ): Promise<{ blob: Blob; dateiname: string; ablage: string | null; ablagePfad: string | null; ablageGrund: string | null }> =>
        api
            .get(`/dokumente/${typ}`, {
                params: ablegen ? { ...parameter, ablegen: 1 } : parameter,
                responseType: 'blob',
            })
            .then(res => ({
                blob: res.data,
                dateiname: dateinameAusKopf(res.headers['content-disposition']) ?? `${typ}.pdf`,
                // Der Ablagezustand steht im Kopf und nicht im Rumpf --
                // der Rumpf ist das PDF.
                ablage: (res.headers['x-ablage-status'] as string) ?? null,
                ablagePfad: entschluesselnOderNull(res.headers['x-ablage-pfad']),
                ablageGrund: entschluesselnOderNull(res.headers['x-ablage-grund']),
            })),
};

/**
 * Die Ebenen der Stammdatenhierarchie, wie der Server sie als Parameterart
 * meldet. Eigener Typ, weil die Berichtsseite ueber sie iteriert (Kaskade:
 * Kunde -> Standort -> Gebaeude -> Stockwerk -> Element) und dabei die
 * Reihenfolge braucht.
 */
export type DokumentEbene = 'kunde' | 'standort' | 'gebaeude' | 'stockwerk' | 'element';

export interface DokumentParameter {
    name: string;
    art: DokumentEbene | 'text';
    pflicht?: boolean;
    bezeichnung: string;
}

export interface DokumentTyp {
    schluessel: string;
    bezeichnung: string;
    beschreibung: string;
    quer: boolean;
    parameter: DokumentParameter[];
}

/** Liest den Dateinamen aus `Content-Disposition`. */
function dateinameAusKopf(kopf: unknown): string | null {
    if (typeof kopf !== 'string') return null;
    const treffer = /filename="([^"]+)"/.exec(kopf);
    return treffer ? treffer[1] : null;
}

function entschluesselnOderNull(wert: unknown): string | null {
    if (typeof wert !== 'string' || wert === '') return null;
    try {
        return decodeURIComponent(wert);
    } catch {
        // Ein unlesbarer Kopf ist kein Grund, den Bericht zu verwerfen.
        return wert;
    }
}

/**
 * Faelligkeiten -- reine Auskunft.
 *
 * Der Endpunkt verschickt bewusst nichts. Das Versenden gehoert dem
 * Zeitplan, und zwar ausschliesslich: koennte ein Aufruf der Uebersicht
 * Mails ausloesen, haetten fuenf geoeffnete Browsertabs fuenf
 * Erinnerungen zur Folge.
 */
export type Terminart = 'WARTUNG' | 'PRUEFUNG';

export interface FaelligerTermin {
    art: Terminart;
    element_id: number;
    element_id_intern: string | null;
    raum: string | null;
    faellig_am: string;
    /** Negative Werte heissen ueberfaellig. */
    tage_bis: number;
    stockwerk: string | null;
    gebaeude: string | null;
    standort: string | null;
    kunde: string | null;
}

export interface FaelligkeitsUebersicht {
    stichtag: string;
    vorwarnzeit_tage: number;
    ueberfaellig: FaelligerTermin[];
    in_30_tagen: FaelligerTermin[];
    in_90_tagen: FaelligerTermin[];
}

export interface Benachrichtigung {
    id: number;
    anlass: string;
    empfaenger: string;
    betreff: string;
    status: string;
    erstellt_am: string;
    versendet_am: string | null;
    fehlertext: string | null;
}

export const faelligkeitenApi = {
    uebersicht: (): Promise<FaelligkeitsUebersicht> =>
        api.get('/faelligkeiten').then(res => res.data),

    /**
     * Was tatsaechlich hinausgegangen ist.
     *
     * Die Frage "wurde erinnert?" ist ohne diese Liste nicht
     * beantwortbar -- eine nicht angekommene Mail hinterlaesst beim
     * Empfaenger keine Spur.
     */
    benachrichtigungen: (grenze = 50): Promise<Benachrichtigung[]> =>
        api.get('/faelligkeiten/benachrichtigungen', { params: { grenze } }).then(res => res.data),
};

export type Wartungsstatus = 'GEPLANT' | 'DURCHGEFUEHRT' | 'UEBERSPRUNGEN';
export type Wartungsergebnis = 'IO' | 'MIT_MAENGELN' | 'NIO';

export interface Wartungsbefund {
    schluessel: string;
    erledigt: boolean;
    bemerkung?: string;
}

export interface Wartung {
    id: number;
    element_id: number;
    vorlage_id: number | null;
    status: Wartungsstatus;
    ergebnis: Wartungsergebnis | null;
    geplant_am: string | null;
    durchgefuehrt_am: string | null;
    durchfuehrender: string | null;
    ergebnisse: Wartungsbefund[];
    bemerkung_kunde: string | null;
    bemerkung_intern: string | null;
    dauer_minuten: number | null;
}

/** Was beim Protokollieren uebergeben wird. */
export interface Protokolleintrag {
    ergebnis: Wartungsergebnis;
    ergebnisse?: Wartungsbefund[];
    bemerkung_kunde?: string | null;
    bemerkung_intern?: string | null;
    dauer_minuten?: number | null;
    durchfuehrender?: string | null;
    durchgefuehrt_am?: string | null;
}

export const wartungenApi = {
    getAll: (filter?: { element_id?: number; status?: Wartungsstatus; kunde_id?: number }): Promise<Wartung[]> =>
        api.get('/wartungen', { params: filter }).then(res => res.data),

    getById: (id: number): Promise<Wartung> =>
        api.get(`/wartungen/${id}`).then(res => res.data),

    create: (daten: Partial<Wartung>): Promise<Wartung> =>
        api.post('/wartungen', daten).then(res => res.data),

    /**
     * Eigener Aufruf und kein PATCH mit `status`.
     *
     * Protokollieren ist eine fachliche Handlung mit eigenem Recht und
     * eigener Wirkung -- sie schreibt den naechsten Termin am Element
     * fort. Als Teilaenderung getarnt wuerde jeder, der Wartungen
     * bearbeiten darf, sie nebenbei ausloesen.
     */
    protokollieren: (id: number, eintrag: Protokolleintrag): Promise<Wartung> =>
        api.post(`/wartungen/${id}/protokollieren`, eintrag).then(res => res.data),

    update: (id: number, daten: Partial<Wartung>): Promise<Wartung> =>
        api.patch(`/wartungen/${id}`, daten).then(res => res.data),

    delete: (id: number) => api.delete(`/wartungen/${id}`).then(res => res.data),
};

// ---------------------------------------------------------------------
// Arbeitsauftraege (PLAN_V4_SAAS_ROADMAP, Phase 3)
//
// Abgrenzung zu Projekten: ein Projekt ist die Begehung, ein
// Arbeitsauftrag die Zuweisung konkreter Arbeit an eine Person zu einer
// Zeit -- siehe backend/database/migrations/V432_2_V433_arbeitsauftraege.sql.
// ---------------------------------------------------------------------

export type ArbeitsauftragStatus = 'GEPLANT' | 'ZUGEWIESEN' | 'IN_ARBEIT' | 'ERLEDIGT';
export type ArbeitsauftragTyp = 'WARTUNG' | 'MAENGELBEHEBUNG' | 'ERSTPRUEFUNG';
export type ArbeitsauftragPrioritaet = 'NIEDRIG' | 'NORMAL' | 'HOCH';
export type ArbeitsauftragUebergang = 'ZUWEISEN' | 'STARTEN' | 'ABSCHLIESSEN';

export interface Arbeitsauftrag {
    id: number;
    nummer: string | null;
    kunde_id: number;
    standort_id: number | null;
    projekt_id: number | null;
    typ: ArbeitsauftragTyp;
    status: ArbeitsauftragStatus;
    prioritaet: ArbeitsauftragPrioritaet;
    beschreibung: string | null;
    geplant_am: string | null;
    faellig_am: string | null;
    zugewiesen_an: number | null;
    abgeschlossen_am: string | null;
    abschlussbemerkung: string | null;
    erstellt_am: string;
    aktualisiert_am: string;
    // Anzeigefelder aus dem Verbund
    kundenname?: string;
    standortname?: string;
    projekt_nr?: string;
    zugewiesen_name?: string | null;
}

export interface ArbeitsauftragVerlaufszeile {
    id: number;
    arbeitsauftrag_id: number;
    uebergang: string;
    alter_status: string | null;
    neuer_status: string;
    grund: string | null;
    geaendert_von: number | null;
    geaendert_am: string;
}

export const arbeitsauftraegeApi = {
    getAll: (filter?: {
        status?: ArbeitsauftragStatus;
        kunde_id?: number;
        zugewiesen_an?: number;
    }): Promise<Arbeitsauftrag[]> =>
        api.get('/arbeitsauftraege', { params: filter }).then((res) => res.data),

    getById: (id: number): Promise<Arbeitsauftrag> =>
        api.get(`/arbeitsauftraege/${id}`).then((res) => res.data),

    getVerlauf: (id: number): Promise<ArbeitsauftragVerlaufszeile[]> =>
        api.get(`/arbeitsauftraege/${id}/verlauf`).then((res) => res.data),

    create: (daten: Partial<Arbeitsauftrag>): Promise<Arbeitsauftrag> =>
        api.post('/arbeitsauftraege', daten).then((res) => res.data),

    update: (id: number, daten: Partial<Arbeitsauftrag>): Promise<{ message: string }> =>
        api.put(`/arbeitsauftraege/${id}`, daten).then((res) => res.data),

    /**
     * Statuswechsel -- der Aufrufer nennt den UEBERGANG, nicht den
     * Zielzustand (services/arbeitsauftragStatus.ts im Backend). Nur bei
     * ZUWEISEN ist `zugewiesen_an` Pflicht.
     */
    statusWechseln: (
        id: number,
        uebergang: ArbeitsauftragUebergang,
        opt?: { zugewiesen_an?: number; grund?: string }
    ): Promise<{ status: ArbeitsauftragStatus; arbeitsauftrag: Arbeitsauftrag }> =>
        api.post(`/arbeitsauftraege/${id}/status`, { uebergang, ...opt }).then((res) => res.data),

    delete: (id: number) => api.delete(`/arbeitsauftraege/${id}`).then((res) => res.data),
};

export const gespraechsnotizenApi = {
    getAll: () => api.get('/gespraechsnotizen').then(res => res.data),
    getById: (id: number) => api.get(`/gespraechsnotizen/${id}`).then(res => res.data),
    getByKunde: (kundeId: number) => api.get(`/gespraechsnotizen/kunde/${kundeId}`).then(res => res.data),
    getByProjekt: (projektId: number) => api.get(`/gespraechsnotizen/projekt/${projektId}`).then(res => res.data),
    getOffeneNachverfolgung: () => api.get('/gespraechsnotizen/nachverfolgung').then(res => res.data),
    create: (data: any) => api.post('/gespraechsnotizen', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/gespraechsnotizen/${id}`, data).then(res => res.data),
    toggleErledigt: (id: number) => api.patch(`/gespraechsnotizen/${id}/erledigt`).then(res => res.data),
    delete: (id: number) => api.delete(`/gespraechsnotizen/${id}`).then(res => res.data),
};

export const ansprechpartnerApi = {
    getAll: () => api.get('/ansprechpartner').then(res => res.data),
    getById: (id: number) => api.get(`/ansprechpartner/${id}`).then(res => res.data),
    getByKunde: (kundeId: number) => api.get(`/ansprechpartner/kunde/${kundeId}`).then(res => res.data),
    getHauptansprechpartner: (kundeId: number) => api.get(`/ansprechpartner/kunde/${kundeId}/haupt`).then(res => res.data),
    create: (data: any) => api.post('/ansprechpartner', data).then(res => res.data),
    update: (id: number, data: any) => api.put(`/ansprechpartner/${id}`, data).then(res => res.data),
    setHauptansprechpartner: (id: number) => api.patch(`/ansprechpartner/${id}/haupt`).then(res => res.data),
    delete: (id: number) => api.delete(`/ansprechpartner/${id}`).then(res => res.data),

    // Notizen
    getNotizen: (ansprechpartnerId: number) => api.get(`/ansprechpartner/${ansprechpartnerId}/notizen`).then(res => res.data),
    getNotizById: (notizId: number) => api.get(`/ansprechpartner/notizen/${notizId}`).then(res => res.data),
    createNotiz: (ansprechpartnerId: number, data: any) => api.post(`/ansprechpartner/${ansprechpartnerId}/notizen`, data).then(res => res.data),
    updateNotiz: (notizId: number, data: any) => api.put(`/ansprechpartner/notizen/${notizId}`, data).then(res => res.data),
    deleteNotiz: (notizId: number) => api.delete(`/ansprechpartner/notizen/${notizId}`).then(res => res.data),
};

// ============================================================================
// QR-Code API (V3)
// ============================================================================

export const zeiterfassungApi = {
    // Get all time entries
    getAll: (filter?: any) =>
        api.get('/zeiterfassung', { params: filter }).then(res => res.data),

    // Get single entry
    getById: (id: number) =>
        api.get(`/zeiterfassung/${id}`).then(res => res.data),

    // Get entries by element
    getByElement: (elementId: number) =>
        api.get(`/zeiterfassung/element/${elementId}`).then(res => res.data),

    // Get entries by project
    getByProjekt: (projektId: number) =>
        api.get(`/zeiterfassung/projekt/${projektId}`).then(res => res.data),

    // Get running timers
    getRunning: (prueferName?: string) =>
        api.get('/zeiterfassung/running', { params: { pruefer_name: prueferName } }).then(res => res.data),

    // Get statistics
    getStatistik: (filter?: any) =>
        api.get('/zeiterfassung/statistik', { params: filter }).then(res => res.data),

    // Start timer
    start: (data: any) =>
        api.post('/zeiterfassung/start', data).then(res => res.data),

    // Stop timer
    stop: (id: number, endzeit?: string) =>
        api.post(`/zeiterfassung/${id}/stop`, { endzeit }).then(res => res.data),

    // Create manual entry
    create: (data: any) =>
        api.post('/zeiterfassung', data).then(res => res.data),

    // Quick add time
    quickAdd: (data: { element_id?: number; projekt_id?: number; minuten: number; aktivitaet_typ?: string; pruefer_name?: string }) =>
        api.post('/zeiterfassung/quick-add', data).then(res => res.data),

    // Update entry
    update: (id: number, data: any) =>
        api.put(`/zeiterfassung/${id}`, data).then(res => res.data),

    // Delete entry
    delete: (id: number) =>
        api.delete(`/zeiterfassung/${id}`).then(res => res.data),
};

export const qrApi = {
    // Get or generate QR code for an element
    getForElement: (elementId: number) =>
        api.get(`/qr/element/${elementId}`).then(res => res.data),

    // Get QR code as SVG for printing
    getSVG: (elementId: number) =>
        api.get(`/qr/element/${elementId}/svg`, { responseType: 'text' }).then(res => res.data),

    // Force regenerate QR code
    generateForElement: (elementId: number) =>
        api.post(`/qr/element/${elementId}/generate`).then(res => res.data),

    // Lookup element by QR code value
    lookupByCode: (codeWert: string) =>
        api.get(`/qr/lookup/${codeWert}`).then(res => res.data),

    // Validate a QR code
    validateCode: (codeWert: string) =>
        api.post('/qr/validate', { codeWert }).then(res => res.data),

    // Batch generate QR codes
    generateBatch: (elementIds: number[]) =>
        api.post('/qr/batch', { elementIds }).then(res => res.data),
};

// ============================================================================
// Plan Verortungen API (V3)
// ============================================================================

export const planVerortungenApi = {
    // Get verortungen by plan
    getByPlan: (planId: number) =>
        api.get(`/plan-verortungen/plan/${planId}`).then(res => res.data),

    // Get verortung by element
    getByElement: (elementId: number) =>
        api.get(`/plan-verortungen/element/${elementId}`).then(res => res.data),

    // Get verortungen by stockwerk
    getByStockwerk: (stockwerkId: number) =>
        api.get(`/plan-verortungen/stockwerk/${stockwerkId}`).then(res => res.data),

    // Get unplaced elements for stockwerk
    getUnplacedElements: (stockwerkId: number) =>
        api.get(`/plan-verortungen/stockwerk/${stockwerkId}/unplaced`).then(res => res.data),

    // Get statistics
    getStatistik: (stockwerkId?: number) =>
        api.get('/plan-verortungen/statistik', { params: { stockwerk_id: stockwerkId } }).then(res => res.data),

    // Create or update verortung
    upsert: (data: { element_id: number; stockwerk_plan_id: number; position_x: number; position_y: number; rotation?: number }) =>
        api.post('/plan-verortungen', data).then(res => res.data),

    // Update position
    updatePosition: (id: number, position_x: number, position_y: number, rotation?: number) =>
        api.put(`/plan-verortungen/${id}`, { position_x, position_y, rotation }).then(res => res.data),

    // Delete verortung
    delete: (id: number) =>
        api.delete(`/plan-verortungen/${id}`).then(res => res.data),

    // Delete by element
    deleteByElement: (elementId: number) =>
        api.delete(`/plan-verortungen/element/${elementId}`).then(res => res.data),

    // Batch upsert
    batchUpsert: (verortungen: Array<{ element_id: number; stockwerk_plan_id: number; position_x: number; position_y: number; rotation?: number }>) =>
        api.post('/plan-verortungen/batch', { verortungen }).then(res => res.data),
};

/**
 * Excel-Aus- und Eingabe ueber die profilbasierte Schicht.
 *
 * Loest den frueheren Altpfad ab. Der Unterschied ist nicht nur die
 * Bibliothek: dort wurde `SELECT *` exportiert -- mit rohen
 * Fremdschluesseln und
 * Datumswerten als Text, und sein Import schrieb ohne Pruefung und ohne
 * Rueckrollen. Hier bestimmt ein Profil die Spalten, und der Import
 * laeuft in zwei Schritten -- pruefen, ansehen, dann uebernehmen.
 */
export interface ExportProfil {
    schluessel: string;
    bezeichnung: string;
    zielgruppe: 'INTERN' | 'KUNDE';
    spalten: number;
}

export interface ImportProfil {
    schluessel: string;
    bezeichnung: string;
    spalten: number;
    schluesselfelder: string[];
}

export interface Zeilenfehler {
    spalte: string;
    excelSpalte: string;
    wert: string;
    regel: string;
    schwere: 'FEHLER' | 'WARNUNG';
    text: string;
    vorschlag?: string;
}

export interface Berichtszeile {
    excel_zeile: number;
    befund: 'NEU' | 'AKTUALISIEREN' | 'UNVERAENDERT' | 'FEHLER' | 'WARNUNG';
    ziel_id: number | null;
    fehler: Zeilenfehler[];
    /** Was sich bei einer Aktualisierung aendert: Feld, vorher, nachher. */
    delta: Array<{ feld: string; vorher: string; nachher: string }>;
    werte?: Record<string, unknown>;
}

export interface Importbericht {
    job: {
        id: number;
        profil: string;
        bezeichnung: string;
        dateiname: string;
        // Genau die Werte des ENUM. `TEILWEISE` und `FEHLER` standen hier
        // aus dem Entwurf -- die Umsetzung kennt nur Alles-oder-Nichts,
        // beide Zustaende entstehen also nie.
        status: 'GEPRUEFT' | 'UEBERNOMMEN' | 'ABGEBROCHEN';
        erstellt_am: string;
        uebernommen_am: string | null;
        laeuft_ab: string;
    };
    strukturfehler: string[];
    zusammenfassung: {
        gesamt: number;
        neu: number;
        aktualisierung: number;
        unveraendert: number;
        fehler: number;
        warnung: number;
    };
    seite: { nummer: number; gesamt: number; nur_fehler: boolean };
    zeilen: Berichtszeile[];
}

export type Importmodus = 'nur_neue' | 'upsert' | 'nur_aktualisieren';

export const excelApi = {
    profile: (): Promise<ExportProfil[]> =>
        api.get('/excel/profile').then(res => res.data),

    importprofile: (): Promise<ImportProfil[]> =>
        api.get('/excel/importprofile').then(res => res.data),

    export: (profil: string, filter?: Record<string, unknown>) =>
        api
            .get(`/excel/export/${profil}`, { params: filter, responseType: 'blob' })
            .then(res => ({
                blob: res.data as Blob,
                dateiname: dateinameAusKopf(res.headers['content-disposition']) ?? `${profil}.xlsx`,
            })),

    vorlage: (profil: string) =>
        api
            .get(`/excel/vorlage/${profil}`, { responseType: 'blob' })
            .then(res => ({
                blob: res.data as Blob,
                dateiname:
                    dateinameAusKopf(res.headers['content-disposition']) ??
                    `Vorlage ${profil}.xlsx`,
            })),

    /** Schritt 1: hochladen und pruefen. Schreibt noch NICHTS. */
    pruefen: (profil: string, datei: File): Promise<Importbericht> => {
        const daten = new FormData();
        daten.append('datei', datei);
        return api
            .post(`/excel/import/${profil}/pruefen`, daten, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            .then(res => res.data);
    },

    /** Schritt 2: den Bericht ansehen, seitenweise. */
    bericht: (
        jobId: number,
        optionen: { nurFehler?: boolean; seite?: number; proSeite?: number } = {}
    ): Promise<Importbericht> =>
        api
            .get(`/excel/import/jobs/${jobId}`, {
                params: {
                    nurFehler: optionen.nurFehler ? 'true' : undefined,
                    seite: optionen.seite,
                    proSeite: optionen.proSeite,
                },
            })
            .then(res => res.data),

    /** Die fehlerhaften Zeilen als Datei -- korrigierbar und erneut hochladbar. */
    fehlerdatei: (jobId: number) =>
        api
            .get(`/excel/import/jobs/${jobId}/fehlerdatei`, { responseType: 'blob' })
            .then(res => ({
                blob: res.data as Blob,
                dateiname: dateinameAusKopf(res.headers['content-disposition']) ?? 'Fehler.xlsx',
            })),

    /** Schritt 3: schreiben. In einer Transaktion. */
    uebernehmen: (
        jobId: number,
        modus: Importmodus,
        fehlerzeilenUeberspringen = false
    ): Promise<{ neu: number; aktualisiert: number; uebersprungen: number; status: string }> =>
        api
            .post(`/excel/import/jobs/${jobId}/uebernehmen`, { modus, fehlerzeilenUeberspringen })
            .then(res => res.data),

    abbrechen: (jobId: number) =>
        api.delete(`/excel/import/jobs/${jobId}`).then(res => res.data),
};


export const backupApi = {
    // List all backups
    getAll: () => api.get('/backup').then(res => res.data),

    // Get database stats
    getStats: () => api.get('/backup/stats').then(res => res.data),

    // Create new backup
    create: () => api.post('/backup/create').then(res => res.data),

    // Download backup
    download: (filename: string) =>
        api.get(`/backup/download/${filename}`, { responseType: 'blob' }).then(res => res.data),

    // Restore from backup
    restore: (filename: string) =>
        api.post(`/backup/restore/${filename}`).then(res => res.data),

    // Upload and restore backup
    upload: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/backup/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => res.data);
    },

    // Delete backup
    delete: (filename: string) =>
        api.delete(`/backup/${filename}`).then(res => res.data),

    // Cleanup old backups
    cleanup: (keepCount: number = 10) =>
        api.post('/backup/cleanup', { keepCount }).then(res => res.data),
};

// ============================================================================
// Wizard API (V4 - Guided Inspection Wizard)
// ============================================================================

export const wizardApi = {
    // Status
    getStatus: (projektId: number) =>
        api.get(`/wizard/projekt/${projektId}/status`).then(res => res.data),
    updateSchritt: (projektId: number, schritt: number) =>
        api.put(`/wizard/projekt/${projektId}/schritt/${schritt}`).then(res => res.data),

    // Schritt 1: Vorbereitung
    getVorbereitung: (projektId: number) =>
        api.get(`/wizard/vorbereitung/${projektId}`).then(res => res.data),
    updateVorbereitungCheckliste: (projektId: number, checkliste: any[]) =>
        api.put(`/wizard/vorbereitung/${projektId}/checkliste`, { checkliste }).then(res => res.data),
    addDokument: (projektId: number, dokument: any) =>
        api.post(`/wizard/vorbereitung/${projektId}/dokumente`, dokument).then(res => res.data),
    deleteDokument: (projektId: number, dokumentId: number) =>
        api.delete(`/wizard/vorbereitung/${projektId}/dokumente/${dokumentId}`).then(res => res.data),

    // Schritt 2: Vorarbeiten
    getVorarbeiten: (projektId: number) =>
        api.get(`/wizard/vorarbeiten/${projektId}`).then(res => res.data),
    updateVorarbeitenCheckliste: (projektId: number, checkliste: any[]) =>
        api.put(`/wizard/vorarbeiten/${projektId}/checkliste`, { checkliste }).then(res => res.data),
    addAbweichung: (projektId: number, abweichung: any) =>
        api.post(`/wizard/vorarbeiten/${projektId}/abweichungen`, abweichung).then(res => res.data),
    deleteAbweichung: (projektId: number, abweichungId: number) =>
        api.delete(`/wizard/vorarbeiten/${projektId}/abweichungen/${abweichungId}`).then(res => res.data),
    addUebersichtsfoto: (projektId: number, foto: any) =>
        api.post(`/wizard/vorarbeiten/${projektId}/fotos`, foto).then(res => res.data),
    deleteUebersichtsfoto: (projektId: number, fotoId: number) =>
        api.delete(`/wizard/vorarbeiten/${projektId}/fotos/${fotoId}`).then(res => res.data),

    // Schritt 3: Begehung
    getChecklisten: (elementTyp?: ElementTyp | 'ALLGEMEIN') =>
        api.get('/wizard/begehung/checklisten', { params: { typ: elementTyp } }).then(res => res.data),
    getBegehungFortschritt: (projektId: number) =>
        api.get(`/wizard/begehung/${projektId}/fortschritt`).then(res => res.data),
    getElementPruefung: (projektId: number, elementId: number) =>
        api.get(`/wizard/begehung/${projektId}/element/${elementId}`).then(res => res.data),
    saveElementPruefung: (projektId: number, elementId: number, pruefung: any) =>
        api.post(`/wizard/begehung/${projektId}/element/${elementId}/pruefung`, pruefung).then(res => res.data),

    // Schritt 4: Auswertung
    getAuswertung: (projektId: number) =>
        api.get(`/wizard/auswertung/${projektId}`).then(res => res.data),

    // Schritt 5: Besprechung
    getBesprechung: (projektId: number) =>
        api.get(`/wizard/besprechung/${projektId}`).then(res => res.data),
    addTeilnehmer: (projektId: number, teilnehmer: any) =>
        api.post(`/wizard/besprechung/${projektId}/teilnehmer`, teilnehmer).then(res => res.data),
    updateTeilnehmer: (projektId: number, teilnehmerId: number, teilnehmer: any) =>
        api.put(`/wizard/besprechung/${projektId}/teilnehmer/${teilnehmerId}`, teilnehmer).then(res => res.data),
    deleteTeilnehmer: (projektId: number, teilnehmerId: number) =>
        api.delete(`/wizard/besprechung/${projektId}/teilnehmer/${teilnehmerId}`).then(res => res.data),
    updateBesprechungNotizen: (projektId: number, notizen: string) =>
        api.put(`/wizard/besprechung/${projektId}/notizen`, { notizen }).then(res => res.data),
    addUnterschrift: (projektId: number, unterschrift: any) =>
        api.post(`/wizard/besprechung/${projektId}/unterschrift`, unterschrift).then(res => res.data),
    deleteUnterschrift: (projektId: number, unterschriftId: number) =>
        api.delete(`/wizard/besprechung/${projektId}/unterschrift/${unterschriftId}`).then(res => res.data),

    // Schritt 6: Nachverfolgung
    getNachverfolgung: (projektId: number) =>
        api.get(`/wizard/nachverfolgung/${projektId}`).then(res => res.data),
    abschliessen: (projektId: number) =>
        api.post(`/wizard/nachverfolgung/${projektId}/abschliessen`).then(res => res.data),
};

export const prueferApi = {
    getAll: (nurAktive: boolean = true) =>
        api.get('/pruefer', { params: { aktiv: nurAktive } }).then(res => res.data),
    getById: (id: number) =>
        api.get(`/pruefer/${id}`).then(res => res.data),
    create: (pruefer: any) =>
        api.post('/pruefer', pruefer).then(res => res.data),
    update: (id: number, pruefer: any) =>
        api.put(`/pruefer/${id}`, pruefer).then(res => res.data),
    delete: (id: number) =>
        api.delete(`/pruefer/${id}`).then(res => res.data),
    deactivate: (id: number) =>
        api.patch(`/pruefer/${id}/deactivate`).then(res => res.data),

    // Projekt-Prüfer
    getByProjekt: (projektId: number) =>
        api.get(`/pruefer/projekt/${projektId}`).then(res => res.data),
    setProjektPruefer: (projektId: number, prueferIds: number[], hauptprueferId?: number) =>
        api.put(`/pruefer/projekt/${projektId}`, { prueferIds, hauptprueferId }).then(res => res.data),
};

// ============ Grundriss-Kompositionen API ============

export const grundrissApi = {
    // Kompositionen
    getByStockwerk: (stockwerkId: number) =>
        api.get('/grundriss-kompositionen', { params: { stockwerkId } }).then(res => res.data),
    getById: (id: number) =>
        api.get(`/grundriss-kompositionen/${id}`).then(res => res.data),
    create: (data: { stockwerk_id: number; bezeichnung?: string; breite?: number; hoehe?: number }) =>
        api.post('/grundriss-kompositionen', data).then(res => res.data),
    update: (id: number, data: any) =>
        api.put(`/grundriss-kompositionen/${id}`, data).then(res => res.data),
    delete: (id: number) =>
        api.delete(`/grundriss-kompositionen/${id}`).then(res => res.data),

    // Layer
    uploadLayer: (kompositionId: number, file: File, bezeichnung?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        if (bezeichnung) formData.append('bezeichnung', bezeichnung);
        return api.post(`/grundriss-kompositionen/${kompositionId}/layer/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then(res => res.data);
    },
    updateLayer: (layerId: number, data: any) =>
        api.put(`/grundriss-kompositionen/layer/${layerId}`, data).then(res => res.data),
    deleteLayer: (layerId: number) =>
        api.delete(`/grundriss-kompositionen/layer/${layerId}`).then(res => res.data),
    updateLayerOrder: (kompositionId: number, layerIds: number[]) =>
        api.put(`/grundriss-kompositionen/${kompositionId}/layer-order`, { layerIds }).then(res => res.data),

    // Marker
    getMarkers: (kompositionId: number) =>
        api.get(`/grundriss-kompositionen/${kompositionId}/marker`).then(res => res.data),
    upsertMarker: (kompositionId: number, data: { element_id: number; position_x: number; position_y: number }) =>
        api.post(`/grundriss-kompositionen/${kompositionId}/marker`, data).then(res => res.data),
    updateMarkerPosition: (markerId: number, position_x: number, position_y: number) =>
        api.put(`/grundriss-kompositionen/marker/${markerId}`, { position_x, position_y }).then(res => res.data),
    deleteMarker: (markerId: number) =>
        api.delete(`/grundriss-kompositionen/marker/${markerId}`).then(res => res.data),

    // Unplaced elements
    getUnplacedElements: (kompositionId: number) =>
        api.get(`/grundriss-kompositionen/${kompositionId}/unplaced-elements`).then(res => res.data),

    // Annotations
    getAnnotations: (kompositionId: number) =>
        api.get(`/grundriss-kompositionen/${kompositionId}/annotationen`).then(res => res.data),
    saveAnnotations: (kompositionId: number, annotations: any[]) =>
        api.put(`/grundriss-kompositionen/${kompositionId}/annotationen`, { annotations }).then(res => res.data),

    // Version history
    getVersions: (kompositionId: number) =>
        api.get(`/grundriss-kompositionen/${kompositionId}/versionen`).then(res => res.data),
    createVersion: (kompositionId: number, bezeichnung: string) =>
        api.post(`/grundriss-kompositionen/${kompositionId}/versionen`, { bezeichnung }).then(res => res.data),
    restoreVersion: (kompositionId: number, versionId: number) =>
        api.post(`/grundriss-kompositionen/${kompositionId}/versionen/${versionId}/restore`).then(res => res.data),
    deleteVersion: (versionId: number) =>
        api.delete(`/grundriss-kompositionen/versionen/${versionId}`).then(res => res.data),

    // Scale
    updateScale: (kompositionId: number, pixelsPerMeter: number | null) =>
        api.put(`/grundriss-kompositionen/${kompositionId}/scale`, { pixelsPerMeter }).then(res => res.data),

    // Export
    exportReport: (kompositionId: number, data: {
        titel?: string; filterTyp?: string; filterStatus?: string; planImageBase64?: string;
        includeLegende?: boolean; includeStatistik?: boolean; includeQrCode?: boolean;
    }) =>
        api.post(`/grundriss-kompositionen/${kompositionId}/export-report`, data, { responseType: 'blob' }).then(res => res.data),
};

// ============================================================================
// Gutachten -- Autorenwerkzeug
// ============================================================================

/**
 * Zustände und Übergänge sind eine WIEDERHOLUNG des Servers
 * (backend/src/services/gutachtenStatus.ts) und nicht die Wahrheit. Die
 * Oberfläche braucht sie nur, um gar nicht erst einen Knopf anzubieten,
 * den der Zustandsautomat abweisen würde — entschieden wird dort.
 */
export const GUTACHTEN_STATUS = [
    'ENTWURF',
    'IN_PRUEFUNG',
    'FREIGEGEBEN',
    'VERSENDET',
    'ZURUECKGEZOGEN',
] as const;
export type GutachtenStatus = (typeof GUTACHTEN_STATUS)[number];

export const GUTACHTEN_UEBERGAENGE = [
    'EINREICHEN',
    'ZURUECKWEISEN',
    'FREIGEBEN',
    'VERSENDEN',
    'ZURUECKZIEHEN',
] as const;
export type GutachtenUebergang = (typeof GUTACHTEN_UEBERGAENGE)[number];

export interface Gutachten {
    id: number;
    nummer: string;
    titel: string;
    kunde_id: number | null;
    projekt_id: number | null;
    gebaeude_id: number | null;
    status: GutachtenStatus;
    anlass: string | null;
    verfasser_id: number | null;
    pruefer_id: number | null;
    freigeber_id: number | null;
    eingereicht_am: string | null;
    geprueft_am: string | null;
    freigegeben_am: string | null;
    versendet_am: string | null;
    zurueckgezogen_am: string | null;
    zurueckgezogen_grund: string | null;
    pdf_hash_sha256: string | null;
    dokument_pfad: string | null;
    eingefroren_am: string | null;
    erstellt_am: string | null;
    aktualisiert_am: string | null;
    /** Nur in der Liste: der Verbund auf `kunden` liefert ihn mit. */
    kunde_name?: string | null;
}

export interface GutachtenAbschnitt {
    id: number;
    gutachten_id: number;
    eltern_id: number | null;
    reihenfolge: number;
    kennung: string;
    titel: string;
    inhalt: string | null;
    /** 0 oder 1 — MariaDB liefert TINYINT, kein Boolean. */
    ki_erzeugt: number;
    ki_modell: string | null;
    bestaetigt_von: number | null;
    bestaetigt_am: string | null;
}

export interface GutachtenKommentar {
    id: number;
    gutachten_id: number;
    abschnitt_id: number | null;
    benutzer_id: number | null;
    text: string;
    erledigt_am: string | null;
    erledigt_von: number | null;
    erstellt_am: string | null;
}

export interface GutachtenVersion {
    id: number;
    gutachten_id: number;
    abschnitt_id: number | null;
    art: 'AUTOSAVE' | 'MANUELL' | 'FREIGABE';
    titel: string | null;
    inhalt: string | null;
    benutzer_id: number | null;
    erstellt_am: string | null;
}

/** Ein Punkt der Freigabe-Prüfliste. Die Meldung kommt wörtlich vom Server. */
export interface GutachtenPruefpunkt {
    schluessel: string;
    /** Ein Blocker verhindert die Freigabe. Eine Warnung nicht. */
    blocker: boolean;
    erfuellt: boolean;
    meldung: string;
}

export interface GutachtenPruefliste {
    freigabefaehig: boolean;
    blocker: GutachtenPruefpunkt[];
    warnungen: GutachtenPruefpunkt[];
}

/** Antwort von POST /:id/status — Ergebnis des Übergangs plus neuer Stand. */
export interface GutachtenUebergangErgebnis {
    status: GutachtenStatus;
    pdf_hash_sha256?: string | null;
    dokument_pfad?: string | null;
    eingefroren_am?: string | null;
    gutachten: Gutachten;
}

export interface GutachtenPruefsumme {
    gutachten_id: number;
    nummer: string;
    verfahren: string;
    pdf_hash_sha256: string;
    eingefroren_am: string | null;
    freigegeben_am: string | null;
    status: GutachtenStatus;
    vorgelegter_hash?: string;
    uebereinstimmung?: boolean;
}

/** Kopfdaten samt Gliederung und Anmerkungen — die Antwort von GET /:id. */
export type GutachtenMitInhalt = Gutachten & {
    abschnitte: GutachtenAbschnitt[];
    kommentare: GutachtenKommentar[];
};

export const gutachtenApi = {
    liste: (filter: { status?: GutachtenStatus; kundeId?: number } = {}): Promise<Gutachten[]> =>
        api.get('/gutachten', { params: filter }).then((r) => r.data),

    holen: (id: number): Promise<GutachtenMitInhalt> =>
        api.get(`/gutachten/${id}`).then((r) => r.data),

    /*
     * Eigener Vorschauweg statt `/dokumente/gutachten/vorschau`.
     * Dieser Endpunkt kommt mit `gutachten.lesen` aus; der gemeinsame
     * verlangt `berichte.erzeugen`, das ein Prüfer nicht haben muss.
     */
    vorschau: (id: number, stand?: string | number): Promise<string> =>
        api.get(`/gutachten/${id}/vorschau`, { params: stand ? { stand } : {} })
            .then((r) => r.data),

    abschnitteUmsortieren: (id: number, kennungen: number[]): Promise<{ geaendert: number }> =>
        api.put(`/gutachten/${id}/abschnitte/reihenfolge`, { kennungen }).then((r) => r.data),

    anlegen: (daten: {
        titel: string;
        nummer?: string;
        kunde_id?: number | null;
        projekt_id?: number | null;
        gebaeude_id?: number | null;
        anlass?: string | null;
    }): Promise<Gutachten> => api.post('/gutachten', daten).then((r) => r.data),

    aendern: (
        id: number,
        daten: {
            titel?: string;
            kunde_id?: number | null;
            projekt_id?: number | null;
            gebaeude_id?: number | null;
            anlass?: string | null;
        }
    ): Promise<Gutachten> => api.patch(`/gutachten/${id}`, daten).then((r) => r.data),

    // ---- Abschnitte ----------------------------------------------------
    abschnittAnlegen: (
        id: number,
        daten: {
            titel: string;
            inhalt?: string | null;
            reihenfolge?: number;
            eltern_id?: number | null;
            ki_erzeugt?: boolean;
            ki_modell?: string | null;
        }
    ): Promise<GutachtenAbschnitt> =>
        api.post(`/gutachten/${id}/abschnitte`, daten).then((r) => r.data),

    /**
     * `autosave` steuert nur, wie der gesicherte Vorstand in der
     * Versionsliste heißt. In zweihundert automatischen Ständen findet
     * niemand den einen, den er bewusst gesichert hat.
     */
    abschnittAendern: (
        id: number,
        abschnittId: number,
        daten: { titel?: string; inhalt?: string | null; reihenfolge?: number },
        autosave = false
    ): Promise<GutachtenAbschnitt> =>
        api
            .patch(`/gutachten/${id}/abschnitte/${abschnittId}`, daten, {
                params: autosave ? { autosave: 1 } : undefined,
            })
            .then((r) => r.data),

    abschnittBestaetigen: (id: number, abschnittId: number): Promise<GutachtenAbschnitt> =>
        api.post(`/gutachten/${id}/abschnitte/${abschnittId}/bestaetigen`).then((r) => r.data),

    abschnittLoeschen: (id: number, abschnittId: number): Promise<void> =>
        api.delete(`/gutachten/${id}/abschnitte/${abschnittId}`).then(() => undefined),

    // ---- Versionen -----------------------------------------------------
    versionen: (id: number, abschnittId?: number): Promise<GutachtenVersion[]> =>
        api
            .get(`/gutachten/${id}/versionen`, { params: abschnittId ? { abschnittId } : undefined })
            .then((r) => r.data),

    wiederherstellen: (id: number, versionId: number): Promise<GutachtenAbschnitt> =>
        api.post(`/gutachten/${id}/versionen/${versionId}/wiederherstellen`).then((r) => r.data),

    // ---- Anmerkungen ---------------------------------------------------
    kommentarAnlegen: (
        id: number,
        daten: { text: string; abschnitt_id?: number | null }
    ): Promise<{ id: number }> => api.post(`/gutachten/${id}/kommentare`, daten).then((r) => r.data),

    kommentarErledigen: (id: number, kommentarId: number): Promise<void> =>
        api.post(`/gutachten/${id}/kommentare/${kommentarId}/erledigt`).then(() => undefined),

    // ---- Prüfliste und Status ------------------------------------------
    pruefliste: (id: number): Promise<GutachtenPruefliste> =>
        api.get(`/gutachten/${id}/pruefliste`).then((r) => r.data),

    statusWechseln: (
        id: number,
        uebergang: GutachtenUebergang,
        grund?: string
    ): Promise<GutachtenUebergangErgebnis> =>
        api.post(`/gutachten/${id}/status`, { uebergang, grund }).then((r) => r.data),

    // ---- Ausgabe -------------------------------------------------------
    /**
     * Das PDF. Bei einem freigegebenen Gutachten kommt es AUS DER ABLAGE
     * und wird nicht neu erzeugt — ein zweiter Lauf hätte einen anderen
     * Zeitstempel und damit eine andere Prüfsumme als die hinterlegte.
     */
    pdf: (id: number): Promise<{ blob: Blob; dateiname: string }> =>
        api.get(`/gutachten/${id}/pdf`, { responseType: 'blob' }).then((res) => ({
            blob: res.data,
            dateiname: dateinameAusKopf(res.headers['content-disposition']) ?? `Gutachten-${id}.pdf`,
        })),

    pruefsumme: (id: number, hash?: string): Promise<GutachtenPruefsumme> =>
        api
            .get(`/gutachten/${id}/pruefsumme`, { params: hash ? { hash } : undefined })
            .then((r) => r.data),
};


// ============================================================================
// Faktura -- Angebote, Rechnungen, DATEV
// ============================================================================

/*
 * Die Aufzählungen sind eine WIEDERHOLUNG des Servers
 * (backend/src/models/angebote.ts, rechnungen.ts) und des Schemas
 * (V422_2_V423_faktura.sql). Sie entscheiden nichts -- der Server wirft
 * unbekannte Werte weg und das ENUM der Spalte lässt sie gar nicht erst
 * zu. Die Oberfläche braucht sie nur, um Filter und Auswahlfelder aus
 * einer Quelle zu bauen statt aus drei handgepflegten Listen.
 */
export const ANGEBOTSSTATUS = [
    'ENTWURF',
    'VERSENDET',
    'ANGENOMMEN',
    'ABGELEHNT',
    'ABGELAUFEN',
] as const;
export type Angebotsstatus = (typeof ANGEBOTSSTATUS)[number];

export const RECHNUNGSSTATUS = [
    'ENTWURF',
    'OFFEN',
    'TEILBEZAHLT',
    'BEZAHLT',
    'STORNIERT',
    'UNEINBRINGLICH',
] as const;
export type Rechnungsstatus = (typeof RECHNUNGSSTATUS)[number];

export const POSITIONSARTEN = ['LEISTUNG', 'MATERIAL', 'TEXT', 'ZWISCHENSUMME'] as const;
export type Positionsart = (typeof POSITIONSARTEN)[number];

export const ZAHLUNGSARTEN = [
    'UEBERWEISUNG',
    'LASTSCHRIFT',
    'BAR',
    'KARTE',
    'VERRECHNUNG',
    'SONSTIGES',
] as const;
export type Zahlungsart = (typeof ZAHLUNGSARTEN)[number];

/**
 * Beträge sind `number | string`, weil MariaDB DECIMAL als Zeichenkette
 * liefert. Das ist kein Schönheitsfehler, den man wegtypisieren sollte:
 * `wert + wert` über eine Rechnung mit dreißig Positionen ergäbe sonst
 * eine Verkettung statt einer Summe. Jeder Wert läuft deshalb durch
 * `alsZahl` aus `components/faktura/betrag.ts`.
 */
export type Betrag = number | string;

export interface Fakturaposition {
    id?: number;
    position?: number;
    art?: Positionsart;
    bezeichnung: string;
    beschreibung?: string | null;
    menge?: Betrag | null;
    einheit?: string | null;
    einzelpreis?: Betrag | null;
    rabatt_prozent?: Betrag | null;
    steuersatz?: Betrag | null;
    /** Vom Server gerechnet -- beim Senden überflüssig. */
    netto?: Betrag;
    /** Nur bei Rechnungspositionen: das DATEV-Erlöskonto. */
    erloeskonto?: string | null;
    element_id?: number | null;
    mangel_id?: number | null;
}

export interface Angebot {
    id: number;
    /** NULL, solange das Angebot Entwurf ist. Siehe `angeboteApi.abschliessen`. */
    nummer: string | null;
    kunde_id: number;
    projekt_id: number | null;
    titel: string;
    status: Angebotsstatus;
    angebotsdatum: string | null;
    gueltig_bis: string | null;
    waehrung: string;
    rabatt_prozent: Betrag;
    netto: Betrag;
    steuer: Betrag;
    brutto: Betrag;
    einleitung: string | null;
    schlusstext: string | null;
    bearbeiter_id: number | null;
    finalisiert_am: string | null;
    versendet_am: string | null;
    entschieden_am: string | null;
    entscheidung_grund: string | null;
    erstellt_am?: string | null;
    /** Nur in der Liste: der Verbund auf `kunden` liefert ihn mit. */
    kunde_name?: string | null;
    /** Nur bei GET /:id. */
    positionen?: Fakturaposition[];
}

export interface Zahlung {
    id: number;
    rechnung_id: number;
    betrag: Betrag;
    zahlungsart: Zahlungsart;
    eingegangen_am: string;
    verwendungszweck: string | null;
    erfasst_von: number | null;
    erfasst_am?: string | null;
}

export interface Rechnung {
    id: number;
    /** NULL, solange die Rechnung Entwurf ist -- die Nummer entsteht beim Finalisieren. */
    nummer: string | null;
    art: 'RECHNUNG' | 'STORNO';
    kunde_id: number;
    projekt_id: number | null;
    angebot_id: number | null;
    vorgaenger_id: number | null;
    status: Rechnungsstatus;
    titel: string;
    rechnungsdatum: string | null;
    leistungsdatum: string | null;
    leistung_von: string | null;
    leistung_bis: string | null;
    faellig_am: string | null;
    zahlungsziel_tage: number;
    waehrung: string;
    netto: Betrag;
    steuer: Betrag;
    brutto: Betrag;
    bezahlt_betrag: Betrag;
    /** 0 oder 1 -- MariaDB liefert TINYINT, kein Boolean. */
    kleinunternehmer: number;
    steuerhinweis: string | null;
    einleitung: string | null;
    schlusstext: string | null;
    empfaenger_name: string | null;
    empfaenger_adresse: string | null;
    aussteller_name: string | null;
    aussteller_adresse: string | null;
    aussteller_ust_id: string | null;
    aussteller_steuernummer: string | null;
    bearbeiter_id: number | null;
    finalisiert_am: string | null;
    versendet_am: string | null;
    bezahlt_am: string | null;
    storniert_am: string | null;
    storno_grund: string | null;
    ausgebucht_am: string | null;
    ausbuchung_grund: string | null;
    dokument_pfad: string | null;
    pdf_hash_sha256: string | null;
    datev_exportiert_am: string | null;
    erstellt_am?: string | null;
    kunde_name?: string | null;
    /** Nur bei GET /:id. */
    positionen?: Fakturaposition[];
    zahlungen?: Zahlung[];
}

/** Ein Punkt der Paragraph-14-UStG-Prüfliste. Die Meldung kommt wörtlich vom Server. */
export interface RechnungPruefpunkt {
    schluessel: string;
    /** Ein Blocker verhindert die Finalisierung. Eine Warnung nicht. */
    blocker: boolean;
    erfuellt: boolean;
    meldung: string;
}

export interface RechnungPruefliste {
    finalisierbar: boolean;
    blocker: RechnungPruefpunkt[];
    warnungen: RechnungPruefpunkt[];
}

export interface Finalisierungsergebnis {
    id: number;
    nummer: string;
    status: string;
    rechnungsdatum: string;
    faellig_am: string;
    pdf_hash_sha256: string;
    dokument_pfad: string;
}

export interface Stornoergebnis {
    storniert: number;
    storno_rechnung_id: number;
    storno_nummer: string;
}

export interface DatevLauf {
    id: number;
    bezeichnung: string;
    von_datum: string;
    bis_datum: string;
    berater_nr: string | null;
    mandanten_nr: string | null;
    sachkontenlaenge: number;
    anzahl_rechnungen: number;
    summe_brutto: Betrag;
    dateiname: string;
    datei_hash_sha256: string | null;
    /** 1, wenn der Lauf bereits Exportiertes bewusst erneut gezogen hat. */
    erneut: number;
    erzeugt_von: number | null;
    erzeugt_am: string;
}

export interface DatevOptionen {
    /** JJJJ-MM-TT, nach Rechnungsdatum. */
    von: string;
    bis: string;
    berater_nr?: string;
    mandanten_nr?: string;
    sachkontenlaenge?: number;
    bezeichnung?: string;
    /** Bereits Exportiertes erneut ziehen -- hebt den Doppelbuchungsschutz auf. */
    erneut?: boolean;
}

/** Das Ergebnis des Probelaufs: Zahlen ohne Datei und ohne Vermerk. */
export interface DatevProbelauf {
    anzahl_rechnungen: number;
    anzahl_buchungen: number;
    summe_brutto: number;
    /** Wie viele Rechnungen im Zeitraum bereits exportiert waren. */
    uebersprungen: number;
    dateiname: string;
}

export type Nummernkreistyp =
    | 'ANGEBOT'
    | 'RECHNUNG'
    | 'STORNO'
    | 'GUTSCHRIFT'
    | 'PROJEKT'
    | 'GUTACHTEN'
    | 'BERICHT';

export type Periodenart = 'NIE' | 'JAEHRLICH' | 'MONATLICH';

export interface Nummernkreis {
    id: number;
    typ: Nummernkreistyp;
    bezeichnung: string;
    praefix: string;
    /** Platzhalter: {PRAEFIX}, {JAHR}, {MONAT}, {NR:4}. */
    format: string;
    periode: Periodenart;
    aktuelle_periode: string;
    letzter_wert: number;
    startwert: number;
    schrittweite: number;
    aktiv: number;
}

/** Eine Zeile des Vergabeprotokolls — die Antwort auf „warum fehlt die 00042". */
export interface Vergabezeile {
    id: number;
    nummer: string;
    laufende_nummer: number;
    periode: string;
    bezug_tabelle: string;
    bezug_id: number | null;
    vergeben_am: string;
    vergeben_von: number | null;
    typ: Nummernkreistyp;
}

export type ObjektTyp = 'KUNDE' | 'STANDORT' | 'GEBAEUDE' | 'PROJEKT';

export const OBJEKT_TYPEN: ObjektTyp[] = ['KUNDE', 'STANDORT', 'GEBAEUDE', 'PROJEKT'];

export interface Zuweisung {
    id: number;
    benutzer_id: number;
    objekt_typ: ObjektTyp;
    objekt_id: number;
    gueltig_von: string;
    /** NULL heißt unbefristet — nicht „abgelaufen". */
    gueltig_bis: string | null;
    erteilt_von: number;
    grund: string;
    widerrufen_am: string | null;
    widerrufen_von: number | null;
    /** Aus dem Verbund, nicht in der Tabelle. */
    vorname: string | null;
    nachname: string | null;
    email: string | null;
    erteiler_vorname: string | null;
    erteiler_nachname: string | null;
}

/**
 * Was der Server tatsächlich durchsetzt.
 *
 * Nicht dieselbe Information wie die Liste der Freischaltungen: der
 * Umfang ist deren aufgelöstes Ergebnis (eine Freischaltung auf einen
 * Kunden erfasst dessen Standorte, Gebäude und Stockwerke mit).
 */
export interface Zuweisungsumfang {
    siehtAlles: boolean;
    kunden: number[];
    standorte: number[];
    gebaeude: number[];
    stockwerke: number[];
    projekte: number[];
}

export const zuweisungenApi = {
    /** Alle Freischaltungen des Mandanten. Verlangt `zuweisungen.lesen`. */
    liste: (filter: { benutzer_id?: number; nur_offene?: boolean } = {}): Promise<Zuweisung[]> =>
        api.get('/zuweisungen', { params: filter }).then((r) => r.data),

    /**
     * Die eigenen Freischaltungen — ohne zusätzliches Recht.
     *
     * Wer wissen will, worauf er gerade Zugriff hat, muss das ohne Umweg
     * können. Ein Monteur, der erst ein Verwaltungsrecht bräuchte, wüsste
     * nie, warum eine Liste leer ist.
     */
    eigene: (): Promise<{ zuweisungen: Zuweisung[]; umfang: Zuweisungsumfang }> =>
        api.get('/zuweisungen/eigene').then((r) => r.data),

    erteilen: (daten: {
        benutzer_id: number;
        objekt_typ: ObjektTyp;
        objekt_id: number;
        gueltig_von?: string | null;
        gueltig_bis: string | null;
        grund: string;
    }): Promise<{ id: number }> => api.post('/zuweisungen', daten).then((r) => r.data),

    /** Widerruft. Die Zeile bleibt als Beleg stehen. */
    widerrufen: (id: number): Promise<void> =>
        api.delete(`/zuweisungen/${id}`).then(() => undefined),
};

export const nummernkreiseApi = {
    liste: (): Promise<{
        kreise: Nummernkreis[];
        typen: Nummernkreistyp[];
        perioden: Periodenart[];
    }> => api.get('/nummernkreise').then((r) => r.data),

    /**
     * Ändert Bezeichnung, Format und Zählweise.
     *
     * `typ` fehlt bewusst: er bestimmt, welcher Beleg aus diesem Kreis
     * zählt, und ihn zu ändern hieße, zwei Belegarten dieselbe Folge
     * teilen zu lassen. Der Server nimmt ihn deshalb gar nicht an.
     */
    aendern: (
        id: number,
        daten: {
            bezeichnung?: string;
            praefix?: string;
            format?: string;
            periode?: Periodenart;
            startwert?: number;
            schrittweite?: number;
            aktiv?: boolean;
        }
    ): Promise<Nummernkreis> => api.patch(`/nummernkreise/${id}`, daten).then((r) => r.data),

    vergaben: (id: number): Promise<Vergabezeile[]> =>
        api.get(`/nummernkreise/${id}/vergaben`).then((r) => r.data),
};

export const angeboteApi = {
    liste: (filter: { kunde_id?: number; status?: Angebotsstatus } = {}): Promise<Angebot[]> =>
        api.get('/angebote', { params: filter }).then((r) => r.data),

    holen: (id: number): Promise<Angebot> => api.get(`/angebote/${id}`).then((r) => r.data),

    anlegen: (daten: {
        kunde_id: number;
        titel: string;
        projekt_id?: number | null;
        angebotsdatum?: string | null;
        gueltig_bis?: string | null;
        rabatt_prozent?: number;
        einleitung?: string | null;
        schlusstext?: string | null;
        positionen?: Fakturaposition[];
    }): Promise<Angebot> => api.post('/angebote', daten).then((r) => r.data),

    /**
     * Ändert Kopf und Positionen.
     *
     * `positionen` ersetzt die gesamte Tabelle -- der Server pflegt keine
     * Zeilen einzeln. Wer nur den Kopf ändern will, lässt das Feld weg;
     * ein leeres Array löscht alle Positionen.
     */
    aendern: (
        id: number,
        daten: {
            titel?: string;
            projekt_id?: number | null;
            angebotsdatum?: string | null;
            gueltig_bis?: string | null;
            rabatt_prozent?: number;
            einleitung?: string | null;
            schlusstext?: string | null;
            positionen?: Fakturaposition[];
        }
    ): Promise<Angebot> => api.patch(`/angebote/${id}`, daten).then((r) => r.data),

    /**
     * Hält fest, wie der Kunde entschieden hat.
     *
     * Eigener Vorgang und kein Feld an `aendern`: nur aus VERSENDET
     * heraus und nur einmal. Als Feld ließe sich die Entscheidung in
     * derselben Anfrage mit einer Titeländerung verstecken.
     *
     * Bei ABGELEHNT ist der Grund Pflicht — „zu teuer" und „Termin passte
     * nicht" führen zu vollkommen verschiedenen Schlüssen für das nächste
     * Angebot.
     */
    entscheidung: (
        id: number,
        status: 'ANGENOMMEN' | 'ABGELEHNT' | 'ABGELAUFEN',
        grund?: string
    ): Promise<Angebot> =>
        api.post(`/angebote/${id}/entscheidung`, { status, grund }).then((r) => r.data),

    /** Hält den Versandzeitpunkt fest. Nur an einem abgeschlossenen Angebot. */
    versenden: (id: number): Promise<Angebot> =>
        api.post(`/angebote/${id}/versenden`).then((r) => r.data),

    loeschen: (id: number): Promise<void> =>
        api.delete(`/angebote/${id}`).then(() => undefined),

    /**
     * Vergibt die Angebotsnummer und setzt den Status auf VERSENDET.
     *
     * Eigene Route und nicht Teil von `aendern`: die Nummer entsteht
     * dabei, und das ist eine Handlung, keine Feldänderung. Sie verlangt
     * `faktura.finalisieren`.
     */
    abschliessen: (id: number): Promise<Angebot> =>
        api.post(`/angebote/${id}/abschliessen`).then((r) => r.data),

    /** Ohne Nummer trägt das PDF das Wasserzeichen ENTWURF. */
    pdf: (id: number): Promise<{ blob: Blob; dateiname: string }> =>
        api.get(`/angebote/${id}/pdf`, { responseType: 'blob' }).then((res) => ({
            blob: res.data,
            dateiname: dateinameAusKopf(res.headers['content-disposition']) ?? `Angebot-${id}.pdf`,
        })),
};

export const rechnungenApi = {
    liste: (filter: { kunde_id?: number; status?: Rechnungsstatus } = {}): Promise<Rechnung[]> =>
        api.get('/rechnungen', { params: filter }).then((r) => r.data),

    holen: (id: number): Promise<Rechnung> => api.get(`/rechnungen/${id}`).then((r) => r.data),

    anlegen: (daten: {
        kunde_id: number;
        titel: string;
        projekt_id?: number | null;
        angebot_id?: number | null;
        leistungsdatum?: string | null;
        leistung_von?: string | null;
        leistung_bis?: string | null;
        zahlungsziel_tage?: number;
        kleinunternehmer?: boolean;
        steuerhinweis?: string | null;
        einleitung?: string | null;
        schlusstext?: string | null;
        positionen?: Fakturaposition[];
    }): Promise<Rechnung> => api.post('/rechnungen', daten).then((r) => r.data),

    /**
     * Ändert Kopf und Positionen -- nur solange die Rechnung ENTWURF ist.
     *
     * Danach antwortet der Server mit 409 und dem Code
     * RECHNUNG_FESTGESCHRIEBEN. Das ist kein fehlendes Recht, sondern der
     * Zustand des Belegs: er liegt beim Kunden und ist gebucht.
     */
    aendern: (
        id: number,
        daten: {
            titel?: string;
            projekt_id?: number | null;
            angebot_id?: number | null;
            leistungsdatum?: string | null;
            leistung_von?: string | null;
            leistung_bis?: string | null;
            zahlungsziel_tage?: number;
            kleinunternehmer?: boolean;
            steuerhinweis?: string | null;
            einleitung?: string | null;
            schlusstext?: string | null;
            positionen?: Fakturaposition[];
        }
    ): Promise<Rechnung> => api.patch(`/rechnungen/${id}`, daten).then((r) => r.data),

    /** Nur Entwürfe. Eine abgeschlossene Rechnung verschwindet nie. */
    loeschen: (id: number): Promise<void> =>
        api.delete(`/rechnungen/${id}`).then(() => undefined),

    /** Die Pflichtangaben-Prüfliste, ohne zu finalisieren. */
    pruefliste: (id: number): Promise<RechnungPruefliste> =>
        api.get(`/rechnungen/${id}/pruefliste`).then((r) => r.data),

    /**
     * ENTWURF -> OFFEN: Nummer vergeben, Angaben einfrieren, PDF ablegen.
     *
     * Nicht zurücknehmbar. Verlangt `faktura.finalisieren`.
     */
    finalisieren: (id: number, rechnungsdatum?: string): Promise<Finalisierungsergebnis> =>
        api.post(`/rechnungen/${id}/finalisieren`, { rechnungsdatum }).then((r) => r.data),

    /** Erzeugt die Stornorechnung mit eigener Nummer. Verlangt `faktura.stornieren`. */
    stornieren: (id: number, grund: string): Promise<Stornoergebnis> =>
        api.post(`/rechnungen/${id}/stornieren`, { grund }).then((r) => r.data),

    /**
     * Hält den Versandzeitpunkt fest.
     *
     * Der Versand selbst läuft heute über das heruntergeladene PDF; hier
     * wird nur festgehalten, wann er stattfand. Ohne diesen Zeitpunkt ist
     * „wann lief die Zahlungsfrist an?" unbeantwortbar — im Mahnfall die
     * erste Frage.
     */
    versenden: (id: number): Promise<Rechnung> =>
        api.post(`/rechnungen/${id}/versenden`).then((r) => r.data),

    /**
     * Bucht eine Forderung als uneinbringlich aus. Verlangt
     * `faktura.stornieren`.
     *
     * Das ist KEIN Storno: eine Ausbuchung sagt „die Forderung bestand zu
     * Recht, sie ist nur nicht einzutreiben", ein Storno sagt „die
     * Rechnung war falsch". Steuerlich zwei verschiedene Vorgänge.
     */
    ausbuchen: (id: number, grund: string): Promise<Rechnung> =>
        api.post(`/rechnungen/${id}/ausbuchen`, { grund }).then((r) => r.data),

    /** Legt die Ersatzrechnung als neuen Entwurf an -- nur nach einem Storno. */
    neuAusstellen: (id: number): Promise<Rechnung> =>
        api.post(`/rechnungen/${id}/neu-ausstellen`).then((r) => r.data),

    /**
     * Bucht einen Zahlungseingang. Auch an einer abgeschlossenen Rechnung
     * erlaubt: der Beleg ändert sich nicht, es kommt ein Vorgang hinzu.
     */
    zahlungErfassen: (
        id: number,
        daten: {
            betrag: number;
            zahlungsart?: Zahlungsart;
            eingegangen_am?: string;
            verwendungszweck?: string | null;
        }
    ): Promise<Rechnung> => api.post(`/rechnungen/${id}/zahlungen`, daten).then((r) => r.data),

    /**
     * Das PDF. Bei einer abgeschlossenen Rechnung kommt es AUS DER ABLAGE
     * und wird nicht neu erzeugt -- sonst passte die hinterlegte Prüfsumme
     * nicht mehr. Fehlt die Datei, antwortet der Server mit 410.
     */
    pdf: (id: number): Promise<{ blob: Blob; dateiname: string }> =>
        api.get(`/rechnungen/${id}/pdf`, { responseType: 'blob' }).then((res) => ({
            blob: res.data,
            dateiname:
                dateinameAusKopf(res.headers['content-disposition']) ?? `Rechnung-${id}.pdf`,
        })),
};

export const datevApi = {
    laeufe: (): Promise<DatevLauf[]> => api.get('/datev/laeufe').then((r) => r.data),

    /** Rechnet den Stapel durch, ohne etwas zu vermerken. Für die Vorschau. */
    probelauf: (opt: DatevOptionen): Promise<DatevProbelauf> =>
        api.post('/datev/buchungsstapel', { ...opt, probelauf: true }).then((r) => r.data),

    /**
     * Erzeugt den Buchungsstapel und markiert die enthaltenen Rechnungen
     * als exportiert.
     *
     * Die Zahlen stehen im Kopf und nicht im Rumpf -- der Rumpf ist die
     * CSV-Datei. `uebersprungen` ist die wichtigste davon: sie sagt, wie
     * viele Rechnungen des Zeitraums der Doppelbuchungsschutz
     * herausgehalten hat.
     */
    erzeugen: (
        opt: DatevOptionen
    ): Promise<{
        blob: Blob;
        dateiname: string;
        anzahlRechnungen: number;
        uebersprungen: number;
    }> =>
        api.post('/datev/buchungsstapel', opt, { responseType: 'blob' }).then((res) => ({
            blob: res.data,
            dateiname:
                dateinameAusKopf(res.headers['content-disposition']) ??
                'EXTF_Buchungsstapel.csv',
            anzahlRechnungen: Number(res.headers['x-anzahl-rechnungen'] ?? 0),
            uebersprungen: Number(res.headers['x-uebersprungen'] ?? 0),
        })),
};

// ============================================================================
// Prüfbereiche und Wartungsvorlagen
// ============================================================================

/**
 * Datentyp eines technischen Felds.
 *
 * Die Werte liegen in einem JSON-Feld (`pruefbereiche.technische_felder`)
 * und damit in keiner Spalte — aus dem Schema ableiten lässt sich der
 * Bereich also nicht. Die Liste hier ist deshalb nur der **Typ**; die
 * anzuzeigenden Werte kommen zur Laufzeit über
 * `pruefbereicheApi.feldtypen()`.
 *
 * Vorher stand hier eine Abschrift aus dem Backend. Eine Abschrift ist
 * genau die Stelle, an der ein Wertebereich veraltet: käme ein sechster
 * Typ dazu, bliebe das Auswahlfeld bei fünf, und es fiele erst auf, wenn
 * jemand ein Feld anlegt, das nicht dargestellt werden kann.
 */
export const FELDTYPEN = ['TEXT', 'ZAHL', 'DATUM', 'JA_NEIN', 'AUSWAHL'] as const;
export type FeldTyp = (typeof FELDTYPEN)[number];

/** Ein technisches Merkmal, das jedes Element dieses Prüfbereichs hat. */
export interface TechnischesFeld {
    schluessel: string;
    bezeichnung: string;
    typ: FeldTyp;
    /** Maßeinheit hinter dem Eingabefeld. */
    einheit?: string;
    /** Nur bei AUSWAHL: die zulässigen Werte. */
    optionen?: string[];
    pflicht?: boolean;
}

export interface Pruefbereich {
    id: number;
    /** NULL kennzeichnet eine Systemvorlage. */
    mandant_id: number | null;
    schluessel: string;
    bezeichnung: string;
    beschreibung: string | null;
    norm_bezug: string | null;
    technische_felder: TechnischesFeld[];
    pruef_intervall_monate: number | null;
    bericht_partial: string | null;
    sortierung: number;
    aktiv: boolean;
    /** Vom Server abgeleitet: mandant_id IS NULL. */
    ist_systemvorlage: boolean;
}

/** Woran ein Prüfbereich hängt — die Grundlage der Stilllegungsfrage. */
export interface Pruefbereichverwendung {
    elemente: number;
    produkte: number;
}

/**
 * Felder, die beim Anlegen und Ändern übergeben werden.
 *
 * `schluessel` steht nur beim Anlegen: an ihm hängt die Zuordnung aus
 * `elemente.typ` und der Vorlagenbaustein des Berichts. Der Server nimmt
 * ihn beim Ändern nicht an (PruefbereicheModel.update).
 */
export interface Pruefbereichdaten {
    bezeichnung?: string;
    beschreibung?: string | null;
    norm_bezug?: string | null;
    technische_felder?: TechnischesFeld[];
    pruef_intervall_monate?: number | null;
    bericht_partial?: string | null;
    sortierung?: number;
    aktiv?: boolean;
}

export const pruefbereicheApi = {
    /** Ohne `alle` nur die aktiven — so wie die Erfassung sie anbietet. */
    liste: (alle = false): Promise<Pruefbereich[]> =>
        api.get('/pruefbereiche', { params: alle ? { alle: 'true' } : {} }).then((r) => r.data),

    holen: (id: number): Promise<Pruefbereich> =>
        api.get(`/pruefbereiche/${id}`).then((r) => r.data),

    /**
     * Die Feldtypen mit Anzeigetext, vom Server.
     *
     * Nicht abgeschrieben: der Wertebereich steckt in einem JSON-Feld und
     * ist aus dem Schema nicht ableitbar — eine Abschrift veraltete
     * unbemerkt.
     */
    feldtypen: (): Promise<{ feldtypen: Array<{ wert: FeldTyp; text: string }> }> =>
        api.get('/pruefbereiche/feldtypen').then((r) => r.data),

    verwendung: (id: number): Promise<Pruefbereichverwendung> =>
        api.get(`/pruefbereiche/${id}/verwendung`).then((r) => r.data),

    anlegen: (daten: Pruefbereichdaten & { schluessel: string; bezeichnung: string }): Promise<Pruefbereich> =>
        api.post('/pruefbereiche', daten).then((r) => r.data),

    aendern: (id: number, daten: Pruefbereichdaten): Promise<Pruefbereich> =>
        api.patch(`/pruefbereiche/${id}`, daten).then((r) => r.data),

    /**
     * Stilllegen, nicht löschen.
     *
     * DELETE setzt `aktiv = FALSE`. Der Bestand bleibt vollständig — ein
     * Element muss auch Jahre später noch erklären können, wonach es
     * geprüft wurde. Die Antwort enthält die Verwendung, damit die
     * Oberfläche danach benennen kann, was betroffen ist.
     */
    stilllegen: (id: number): Promise<{ erfolg: boolean; verwendung: Pruefbereichverwendung }> =>
        api.delete(`/pruefbereiche/${id}`).then((r) => r.data),
};

/** Ein Punkt der Checkliste, die bei der Wartung abgearbeitet wird. */
export interface Taetigkeit {
    schluessel: string;
    text: string;
    pflicht?: boolean;
    hinweis?: string;
}

export interface Wartungsvorlage {
    id: number;
    bezeichnung: string;
    beschreibung: string | null;
    pruefbereich_id: number | null;
    taetigkeiten: Taetigkeit[];
    intervall_monate: number;
    /** NULL: es gilt die Vorwarnzeit des Mandanten. */
    vorwarnzeit_tage: number | null;
    aktiv: boolean;
}

export interface Wartungsvorlagendaten {
    bezeichnung?: string;
    beschreibung?: string | null;
    pruefbereich_id?: number | null;
    taetigkeiten?: Taetigkeit[];
    intervall_monate?: number;
    vorwarnzeit_tage?: number | null;
    aktiv?: boolean;
}

/**
 * Der Server lässt nur 1 bis 240 Monate zu
 * (WartungsvorlagenController.intervallGueltig). Ein Intervall von 0 wäre
 * kein Termin, sondern eine Dauerschleife.
 */
export const INTERVALL_MIN = 1;
export const INTERVALL_MAX = 240;

/** Was an einer Wartungsvorlage hängt — vor dem Stilllegen die einzige belastbare Auskunft. */
export interface Wartungsvorlagenverwendung {
    elemente: number;
    /** Nur die geplanten; durchgeführte sind Nachweise und ändern sich nicht. */
    geplante_wartungen: number;
}

export const wartungsvorlagenApi = {
    liste: (alle = false): Promise<Wartungsvorlage[]> =>
        api.get('/wartungsvorlagen', { params: alle ? { alle: 'true' } : {} }).then((r) => r.data),

    verwendung: (id: number): Promise<Wartungsvorlagenverwendung> =>
        api.get(`/wartungsvorlagen/${id}/verwendung`).then((r) => r.data),

    holen: (id: number): Promise<Wartungsvorlage> =>
        api.get(`/wartungsvorlagen/${id}`).then((r) => r.data),

    anlegen: (daten: Wartungsvorlagendaten & { bezeichnung: string }): Promise<Wartungsvorlage> =>
        api.post('/wartungsvorlagen', daten).then((r) => r.data),

    aendern: (id: number, daten: Wartungsvorlagendaten): Promise<Wartungsvorlage> =>
        api.patch(`/wartungsvorlagen/${id}`, daten).then((r) => r.data),

    /**
     * Stilllegen, nicht löschen: an einer Vorlage hängen abgeschlossene
     * Wartungsprotokolle, die weiterhin erklären müssen, wonach gewartet
     * wurde.
     */
    stilllegen: (id: number): Promise<Wartungsvorlage> =>
        api.delete(`/wartungsvorlagen/${id}`).then((r) => r.data),
};

/* ------------------------------------------------------------------ */
/* Wissensdatenbank (Phase 5b)                                         */
/* ------------------------------------------------------------------ */

/**
 * Sichtbarkeitsstufe eines Artikels.
 *
 * Die Vorgabe ist INTERN und nicht KUNDE: ein Wissensartikel ist
 * Hauswissen. Wer ihn herausgeben will, sagt das ausdrücklich.
 */
export type Wissenssichtbarkeit = 'KUNDE' | 'INTERN' | 'VERTRAULICH';

/**
 * Woran ein Artikel hängt — und damit, wo er auftaucht.
 *
 * PRUEFBEREICH  Schlüssel aus `pruefbereiche` (TUER, SCHOTT, …)
 * PRUEFPUNKT    Kennung eines Prüfpunkts aus den Checklisten
 * MANGELKATEGORIE  `maengel.kategorie`, wie sie am Mangel steht
 */
export type Wissensbezugsart = 'PRUEFBEREICH' | 'PRUEFPUNKT' | 'MANGELKATEGORIE';

export const WISSENSBEZUGSARTEN: Wissensbezugsart[] = [
    'PRUEFBEREICH',
    'PRUEFPUNKT',
    'MANGELKATEGORIE',
];

export interface Wissensbezug {
    art: Wissensbezugsart;
    ziel: string;
}

export interface Wissensartikel {
    id: number;
    kategorie: string;
    titel: string;
    kurzfassung: string | null;
    inhalt_html: string | null;
    schlagworte: string[];
    /** Verweis in das Normenregister — der Artikel enthält keinen Normtext. */
    normung_id: number | null;
    norm_ausgabe: string | null;
    norm_abschnitt: string | null;
    /** Verweis auf die Berichtsformulierung, nicht deren Kopie. */
    textbaustein_id: number | null;
    sichtbarkeit: Wissenssichtbarkeit;
    version: number;
    aktiv: boolean;
    erstellt_am?: string;
    aktualisiert_am?: string;
    /** Nur bei der Einzelansicht gefüllt. */
    bezuege?: Wissensbezug[];
    /** Nur in Trefferlisten: Rang aus dem Volltextindex. */
    relevanz?: number;
}

export interface Wissensartikeldaten {
    kategorie?: string;
    titel?: string;
    kurzfassung?: string | null;
    inhalt_html?: string | null;
    schlagworte?: string[];
    normung_id?: number | null;
    norm_ausgabe?: string | null;
    norm_abschnitt?: string | null;
    textbaustein_id?: number | null;
    sichtbarkeit?: Wissenssichtbarkeit;
    aktiv?: boolean;
    bezuege?: Wissensbezug[];
}

export const wissenApi = {
    /**
     * Liste und Suche über denselben Endpunkt.
     *
     * Bewusst nicht getrennt: eine Suche mit eigener Adresse liefert
     * erfahrungsgemäß irgendwann andere Felder als die Liste.
     */
    suchen: (filter: {
        suche?: string;
        kategorie?: string;
        auch_stillgelegte?: boolean;
        limit?: number;
    } = {}): Promise<Wissensartikel[]> =>
        api
            .get('/wissensartikel', {
                params: {
                    ...filter,
                    auch_stillgelegte: filter.auch_stillgelegte ? 'true' : undefined,
                },
            })
            .then((r) => r.data),

    /**
     * Die Hinweise zu einem Prüfbereich, Prüfpunkt oder eine Mangelkategorie.
     *
     * Der eigentliche Zweck der Wissensdatenbank: ein Bestand, den man
     * nur über eine eigene Liste erreicht, wird während der Begehung
     * nicht gelesen.
     */
    kontext: (art: Wissensbezugsart, ziel: string): Promise<Wissensartikel[]> =>
        api.get('/wissensartikel/kontext', { params: { art, ziel } }).then((r) => r.data),

    kategorien: (): Promise<Array<{ kategorie: string; anzahl: number }>> =>
        api.get('/wissensartikel/kategorien').then((r) => r.data),

    holen: (id: number): Promise<Wissensartikel> =>
        api.get(`/wissensartikel/${id}`).then((r) => r.data),

    anlegen: (daten: Wissensartikeldaten & { titel: string }): Promise<Wissensartikel> =>
        api.post('/wissensartikel', daten).then((r) => r.data),

    aendern: (id: number, daten: Wissensartikeldaten): Promise<Wissensartikel> =>
        api.patch(`/wissensartikel/${id}`, daten).then((r) => r.data),

    /**
     * Stilllegen, nicht löschen: ein Prüfhinweis, nach dem jemand
     * gearbeitet hat, muss nachlesbar bleiben — sonst lässt sich ein
     * alter Befund nicht mehr erklären.
     */
    stilllegen: (id: number): Promise<void> =>
        api.delete(`/wissensartikel/${id}`).then(() => undefined),
};

// =====================================================================
// KI-Schicht (Nutzung, Obergrenze, Governance)
// =====================================================================

export interface KiEinstellungAnzeige {
    anwendungsfall: string;
    bezeichnung: string;
    recht: string;
    aktiv: boolean;
    anbieter: 'MISTRAL' | 'ANTHROPIC';
    modell: string;
    preis_geschaetzt: boolean;
}

export interface KiEinstellungen {
    anwendungsfaelle: KiEinstellungAnzeige[];
    grenze: { token: number | null; cent: number | null };
    verbrauch: { token: number; cent: number; aufrufe: number };
    geheimnis_maskierung: boolean;
}

export interface KiNutzungAuswertung {
    monat: { token: number; cent: number; aufrufe: number };
    grenze: { token: number | null; cent: number | null };
    je_anwendungsfall: Array<{
        anwendungsfall: string;
        anbieter: string;
        modell: string;
        aufrufe: number;
        fehlversuche: number;
        eingabe_token: number;
        ausgabe_token: number;
        kosten_cent: number;
    }>;
    letzte: Array<{
        id: number;
        zeitpunkt: string;
        benutzer_id: number | null;
        anwendungsfall: string;
        anbieter: string;
        modell: string;
        eingabe_token: number;
        ausgabe_token: number;
        dauer_ms: number;
        kosten_cent: number;
        erfolg: boolean | number;
        fehler_code: string | null;
        fehler_meldung: string | null;
    }>;
}

export const kiApi = {
    einstellungenLesen: (): Promise<KiEinstellungen> =>
        api.get('/ki/einstellungen').then((r) => r.data),

    /** `grenze` liefert dieselbe Zahl auf beiden — kein zweiter Round-Trip nötig. */
    nutzung: (grenze = 50): Promise<KiNutzungAuswertung> =>
        api.get('/ki/nutzung', { params: { grenze } }).then((r) => r.data),

    grenzeSetzen: (grenze: { token: number | null; cent: number | null }): Promise<KiEinstellungen> =>
        api.put('/ki/einstellungen/grenze', grenze).then((r) => r.data),

    maskierungSetzen: (aktiv: boolean): Promise<KiEinstellungen> =>
        api.put('/ki/einstellungen/maskierung', { aktiv }).then((r) => r.data),

    anwendungsfallSetzen: (
        anwendungsfall: string,
        daten: { anbieter?: string; modell?: string | null; aktiv?: boolean }
    ): Promise<KiEinstellungen> =>
        api.put(`/ki/einstellungen/${anwendungsfall}`, daten).then((r) => r.data),

    // Zugaenge: API-Schluessel je KI-Anbieter (MISTRAL/ANTHROPIC). Eigene
    // Rechtegruppe (ki.verwalten) -- wer die KI nutzen darf, darf noch
    // lange keinen Schluessel hinterlegen oder auf Kosten des Mandanten
    // einen Testaufruf ausloesen.
    zugaengeLesen: (): Promise<KiZugang[]> =>
        api.get('/ki/zugaenge').then((r) => r.data),

    zugangSetzen: (
        anbieter: string,
        daten: { schluessel?: string; standard_modell?: string; aktiv?: boolean }
    ): Promise<KiZugang[]> =>
        api.put(`/ki/zugaenge/${anbieter}`, daten).then((r) => r.data),

    /** Ruft tatsaechlich beim Anbieter an und verursacht damit Kosten. */
    zugangTesten: (anbieter: string): Promise<{ erfolg: boolean; meldung: string }> =>
        api.post(`/ki/zugaenge/${anbieter}/test`).then((r) => r.data),

    zugangEntfernen: (anbieter: string): Promise<void> =>
        api.delete(`/ki/zugaenge/${anbieter}`).then((r) => r.data),
};

export interface KiZugang {
    anbieter: string;
    vorgabe_modell: string;
    modelle: string[];
    eingerichtet: boolean;
    schluessel_maske: string | null;
    standard_modell: string;
    aktiv: boolean;
    letzter_test_am: string | null;
    letzter_test_erfolg: boolean | null;
    letzter_test_meldung: string | null;
}

export type SucheEntitaet =
    | 'KUNDEN'
    | 'STANDORTE'
    | 'ELEMENTE'
    | 'MAENGEL'
    | 'PROJEKTE'
    | 'GUTACHTEN'
    | 'WARTUNGEN'
    | 'ARBEITSAUFTRAEGE'
    | 'RECHNUNGEN'
    | 'ANGEBOTE'
    | 'PRODUKTKATALOG';

export interface SucheTreffer {
    entitaet: SucheEntitaet;
    id: number;
    titel: string;
    kontext: string;
}

export type SucheErgebnis = Record<SucheEntitaet, SucheTreffer[]>;

export const sucheApi = {
    suchen: (q: string): Promise<SucheErgebnis> =>
        api.get('/suche', { params: { q } }).then((r) => r.data),
};

/**
 * Sync-Konflikte der mobilen Synchronisation (PLAN_MOBILE_APP.md M.8).
 *
 * `liste()` ruft denselben Endpunkt wie die mobile Konfliktliste auf, aber
 * mit `alle=1` -- das liefert alle offenen Konflikte des Mandanten statt
 * nur die des angemeldeten Benutzers und verlangt dafuer
 * `system.sync_konflikte_verwalten` (mobileSyncController.konflikteListe).
 */
export interface SyncKonflikt {
    id: number;
    tabelle: string;
    datensatz_id: number;
    feld: string;
    server_wert: string | null;
    client_wert: string | null;
    client_basis_wert: string | null;
    entstanden_am: string;
    status: 'OFFEN' | 'GELOEST';
    benutzer_id: number;
    benutzer_name: string | null;
    benutzer_email: string | null;
}

export const syncKonflikteApi = {
    liste: (): Promise<SyncKonflikt[]> =>
        api.get('/mobile-sync/konflikte', { params: { alle: '1' } }).then((r) => r.data),

    loesen: (
        id: number,
        loesung: 'LOKAL' | 'SERVER' | 'MANUELL',
        manuellerWert?: string | null
    ): Promise<void> =>
        api
            .post(`/mobile-sync/konflikte/${id}/loesen`, {
                loesung,
                manueller_wert: manuellerWert ?? null,
            })
            .then(() => undefined),
};

export default api;
